import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { Wallet, Search, Plus, Minus, Eye, EyeOff, Trash2, Ban, Printer, Send, Check, MessageCircle, Loader2, FileText, FileDown, History, Undo2, Barcode } from 'lucide-react';
import { api, getToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import {
  formatMoeda, formatMoedaSemSimbolo, formatDataSP,
  ehBaixaParcial, ehTituloDeSaldo, jaPagoAntesDoSaldo, chaveDoRecibo, TOLERANCIA_CENTAVO_CALIBRACAO,
} from '@fiodecobre/shared';
import {
  gerarReciboHTML, gerarReciboJpg, gerarExtratoHTML, gerarExtratoJpg, imprimirHTML,
  type ReciboParams, type ContaParaExtrato, type ExtratoData,
} from './utils/extrato-html';
import { ModalEnviarWhatsAppCliente } from '../../components/whatsapp/ModalEnviarWhatsAppCliente';
import { ModalBuscarCliente } from '../../components/orcamento/ModalBuscarCliente';
import { ExtratoCliente } from '../cadastros/ExtratoCliente';
import { CobrancaPix } from '../../components/caixa/CobrancaPix';
import { CobrancaCartao, ImprimirViaCartao } from '../../components/caixa/CobrancaCartao';
import { SimulacaoParcelamentoWhatsApp, type ResultadoParcelamento, type EstadoParcelamento } from '../../components/pagamento/SimulacaoParcelamentoWhatsApp';
import { TitulosParaReceber } from '../../components/contas-receber/TitulosParaReceber';
import { ModalBoletoTitulo } from '../../components/contas-receber/ModalBoletoTitulo';

interface ContaReceber {
  id: number;
  orcamento_id: number | null;
  orcamento_numero: string | null;
  orcamento_total: string | null;
  cliente_id: number;
  cliente_nome: string;
  cliente_cpf_cnpj: string | null;
  cliente_telefone: string | null;
  cliente_codigo_legado: string | null;
  valor: string;
  valor_original: string | null;
  data_vencimento: string;
  data_pagamento: string | null;
  data_emissao: string;
  valor_pago: string | null;
  forma_pagamento_id: number | null;
  forma_pagamento_nome: string | null;
  status: string;
  observacoes: string | null;
  exibir_no_caixa: boolean;
  vencido: boolean;
  dias_atraso: number;
  origem_legado: string | null;
  operador_baixa_nome: string | null;
  vendedor_id: number | null;
  vendedor_efetivo_id: number | null;
  vendedor_nome: string | null;
  /** Id do boleto VIVO deste titulo, ou `null`. So decide o estado do botao — o
   *  detalhe vem do `GET /:id/boleto` quando o modal abre ([DA-629]). */
  boleto_id: number | null;
  /** A nota fiscal do pedido ([DA-632]). ⚠️ O TIPO nao e' enfeite: 243 numeros existem
   *  nas DUAS series, entao o numero sozinho nao identifica a nota. `null` nos dois
   *  campos quando nao ha nota autorizada (149 dos 390 titulos: legado e consignacao). */
  nota_tipo: 'nfe' | 'nfce' | null;
  nota_numero: number | null;
  /** Instante da baixa, da trilha ([DA-514]). E' o que agrupa o recibo — ver `chaveDoRecibo`
   *  em `@fiodecobre/shared`. `null` so em titulo pendente (a trilha cobre 367 de 367 pagos). */
  baixado_em: string | null;
  /** O que o CLIENTE pagou, quando difere de `valor_pago` (acrescimo do cartao rateado, 0321).
   *  `null` = igual ao valor_pago. Coluna "Valor Pago" e coluna Pago do recibo; a comissao NAO le. */
  valor_pago_cliente?: string | null;
}

interface FormaPagamento {
  id: number;
  nome: string;
  tipo: string;
}

const KEY_LAST_CAIXA = 'contas_receber.last_caixa';
interface CaixaMin { id: number; nome: string; tipo: string }

// Um recebimento LIVRE disponivel pra casar com a baixa: Pix do MP (DA-221) OU recebimento avulso
// (dinheiro/cartao, Receber Avulso). `origem` decide qual id viaja na baixa (mp_recebimento_id vs
// recebimento_avulso_id) — o backend consome sem criar dinheiro novo no caixa.
interface RecebimentoLivre {
  origem: 'pix' | 'avulso';
  id: number;
  forma_tipo: string | null; // pix | dinheiro | cartao_credito | cartao_debito
  forma_pagamento_id: number | null; // forma da baixa (Pix do MP = forma Pix; avulso = sua forma)
  caixa_id: number | null; // conta onde o dinheiro JÁ caiu (Mercado Pago no Pix; gaveta no avulso)
  caixa_nome: string | null;
  valor: string;
  disponivel: string;
  descricao_origem: string | null; // pagador (Pix) ou nome da forma (avulso)
  quando: string | null;
  mesmo_valor: boolean;
}

const rotuloFormaLivre = (r: RecebimentoLivre): string => {
  if (r.origem === 'pix') return 'Pix';
  switch (r.forma_tipo) {
    case 'dinheiro': return 'Dinheiro';
    case 'cartao_credito': return 'Crédito';
    case 'cartao_debito': return 'Débito';
    default: return 'Avulso';
  }
};

// Quanto do recebimento sobra depois dos titulos E pode ser marcado como acrescimo do cartao
// (migration 0320): so recebimento AVULSO de cartao, maior que o total. 0 = a opcao nao aparece.
// Peca unica: decide se a caixa aparece no seletor E se a baixa manda a marca pro servidor.
const restanteAcrescimoCartao = (sel: RecebimentoLivre | null, totalTitulos: number): number => {
  if (!sel || sel.origem !== 'avulso') return 0;
  if (sel.forma_tipo !== 'cartao_credito' && sel.forma_tipo !== 'cartao_debito') return 0;
  const resto = +(Number(sel.disponivel) - totalTitulos).toFixed(2);
  return resto > 0.005 ? resto : 0;
};

// O que o CLIENTE pagou por um titulo baixado: valor_pago_cliente quando houve acrescimo do cartao
// (0321), senao o proprio valor_pago. Peca unica da coluna "Valor Pago" e do recibo.
const valorPagoCliente = (c: { valor_pago_cliente?: string | null; valor_pago?: string | number | null; valor: string | number }): number =>
  Number(c.valor_pago_cliente ?? c.valor_pago ?? c.valor);

// Forma escolhida e Pix? (o backend identifica por tipo='pix')
const ehFormaPix = (formas: FormaPagamento[], formaId: string) =>
  formas.find((f) => String(f.id) === String(formaId))?.tipo === 'pix';

// Bloco "Vincular ao recebimento livre" — compartilhado pelos 2 modais de baixa (single + lote).
// Lista os recebimentos LIVRES do cliente (Pix do MP nao conferidos + recebimentos avulsos de
// dinheiro/cartao), mesmo valor no topo. Operador escolhe 1 (clicar de novo desmarca). Parcial ->
// aviso. Casar NAO cria dinheiro novo no caixa — so amarra o recebimento que ja entrou.
function SeletorRecebimentoLivre({ recebimentos, selecionado, onSelecionar, totalTitulos, caixaNome, escapeVisivel, acrescimo, onAcrescimo }: {
  recebimentos: RecebimentoLivre[] | null;
  selecionado: RecebimentoLivre | null;
  onSelecionar: (p: RecebimentoLivre | null) => void;
  totalTitulos: number;
  /** "O restante e' acrescimo do cartao" marcado? Nasce desmarcado — quem decide e' o operador. */
  acrescimo: boolean;
  onAcrescimo: (v: boolean) => void;
  /** Conta escolhida no modal — a lista ja vem filtrada por ela; aqui so nomeia o vazio. */
  caixaNome?: string | null;
  /** O escape "Ja recebido — so registrar" esta na tela? So entao a mensagem pode mandar usa-lo. */
  escapeVisivel?: boolean;
}) {
  if (recebimentos === null) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0' }}>Buscando recebimentos livres...</div>;
  }
  const disp = (p: RecebimentoLivre) => Number(p.disponivel);
  const selDisp = selecionado ? disp(selecionado) : 0;
  // Sobra: recebimento MAIOR que o total (ok — o excedente fica livre pra outro pedido). A FALTA
  // (recebimento menor) é barrada no rodapé do modal — não dá pra quitar além do que ele cobre.
  // Sobra de até 5 centavos não avisa: o servidor absorve e fecha o Pix [DA-817].
  const sobra = !!selecionado && Math.round((selDisp - totalTitulos) * 100) / 100 > TOLERANCIA_CENTAVO_CALIBRACAO;
  const restanteCartao = restanteAcrescimoCartao(selecionado, totalTitulos);
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, padding: 10,
      background: 'var(--card-bg)', display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>Vincular a um recebimento livre (Pix / avulso)</div>
      {recebimentos.length === 0 ? (
        // Vazio se EXPLICA, nunca some: sumir calado foi o que fez o operador concluir que nao ha
        // como conciliar. Com conta escolhida, a frase nomeia a conta — o sistema so casa o que
        // passou por ele (Pix do Mercado Pago e recebimento avulso); Pix direto na Cora/BB nao tem
        // feed pra bater, e o caminho de la e o escape "Ja recebido". Ver [DA-593].
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {caixaNome
            ? `Nenhum recebimento livre em ${caixaNome}. O sistema só casa o que passou por ele (Pix do Mercado Pago e recebimentos avulsos).`
            : 'Nenhum recebimento livre pra este cliente. A baixa segue normal.'}
          {/* A saida vai com o rotulo LITERAL do checkbox e com peso proprio: recusa que nao alcanca
              quem a le vira beco (DA-560). */}
          {escapeVisivel && (
            <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>
              {' '}Pra dar baixa de um pagamento que caiu direto nessa conta, marque <strong>“Já recebido — só registrar”</strong> abaixo.
            </span>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Escolha o recebimento que de fato caiu pra abater sem contar duas vezes. Opcional — sem escolher, a baixa segue normal.
          </div>
          <div style={{ maxHeight: 168, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {recebimentos.map((p) => {
              const sel = selecionado?.origem === p.origem && selecionado?.id === p.id;
              return (
                <button
                  key={`${p.origem}-${p.id}`}
                  type="button"
                  onClick={() => onSelecionar(sel ? null : p)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', borderRadius: 6, padding: '6px 10px',
                    border: sel ? '2px solid #d97706' : '1px solid var(--border)',
                    background: sel ? '#fde68a' : (p.mesmo_valor ? 'rgba(253,230,138,0.35)' : 'var(--input-bg)'),
                    color: sel ? '#000' : 'var(--text-color)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                      {formatMoeda(disp(p))}
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: sel ? '#000' : '#b45309' }}>{rotuloFormaLivre(p)}</span>
                      {p.mesmo_valor && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: sel ? '#000' : '#b45309' }}>= valor a receber</span>}
                    </span>
                    <span style={{ fontSize: 11, color: sel ? '#000' : 'var(--text-muted)' }}>
                      {p.quando ? formatDataSP(p.quando) : '—'} · {p.descricao_origem || 'origem nao informada'}
                    </span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{sel ? '✓ Escolhido' : 'Escolher'}</span>
                </button>
              );
            })}
          </div>
          {sobra && (
            <div style={{ background: '#fde68a', color: '#000', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600 }}>
              {restanteCartao > 0 && acrescimo
                ? `O recebimento (${formatMoeda(selDisp)}) e maior que o total a receber (${formatMoeda(totalTitulos)}). Os ${formatMoeda(restanteCartao)} restantes ficam registrados como acrescimo do cartao — nao ficam livres pra outro titulo.`
                : `O recebimento (${formatMoeda(selDisp)}) e maior que o total a receber (${formatMoeda(totalTitulos)}). A sobra fica livre pra casar com outro pedido depois.`}
              {restanteCartao > 0 && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, cursor: 'pointer', fontWeight: 700 }}>
                  <input type="checkbox" checked={acrescimo} onChange={(e) => onAcrescimo(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>Os {formatMoeda(restanteCartao)} restantes sao acrescimo do cartao (o cliente pagou a taxa por cima)</span>
                </label>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Caixa de forma+caixa TRAVADOS: quando o operador casa a baixa com um recebimento livre, a forma e a
// conta vêm dele (o dinheiro já entrou com forma/destino definidos) — não se redigita nem se contradiz.
// Substitui os seletores de Forma/Caixa enquanto há recebimento escolhido. Ver [[feedback_baixa_dinheiro_conta_visivel]].
function FormaCaixaTravada({ rec }: { rec: RecebimentoLivre }) {
  return (
    <div style={{
      flex: '1 1 420px',
      border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
      background: 'rgba(253,230,138,0.35)', display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
        Forma e caixa definidos pelo recebimento escolhido
      </div>
      <div style={{ fontSize: 14, fontWeight: 700 }}>
        {rotuloFormaLivre(rec)} · {rec.caixa_nome || '—'}
      </div>
    </div>
  );
}

// Converte data do banco (DATE serializada como '...T00:00:00.000Z' ou 'YYYY-MM-DD')
// pro formato do <input type="date"> sem sofrer deslocamento de fuso.
// Trilha de auditoria do titulo (migration 0210). `origem_registro` distingue o que foi gravado no
// ato do que o backfill reconstruiu — hora reconstruida a partir da data e' ESTIMADA e vai rotulada.
type EventoCR = {
  id: number;
  acao: 'baixa' | 'estorno' | 'cancelamento' | 'vencimento' | 'vendedor';
  quando: string;
  valor: string | number | null;
  gatilho: string | null;
  ip: string | null;
  motivo: string | null;
  detalhe: any;
  origem_registro: string;
  usuario_nome: string | null;
  forma_nome: string | null;
  caixa_nome: string | null;
};

const ROTULO_ACAO: Record<string, string> = {
  baixa: 'Baixa (recebimento)',
  estorno: 'Estorno da baixa',
  cancelamento: 'Cancelamento',
  vencimento: 'Vencimento alterado',
  vendedor: 'Vendedor alterado',
};

const ROTULO_GATILHO: Record<string, string> = {
  manual: 'botao do titulo',
  lote: 'baixa em lote',
  conciliacao: 'conciliacao bancaria',
  fechamento_cm: 'fechamento Venda CM',
};

/**
 * A NOTA FISCAL do titulo ([DA-632], 27/08/2026) — pedido dele, com print marcando a
 * posicao da coluna entre `#` e `Cod`: *"uma coluna com o numero da nota fiscal. A mesma
 * coluna exibiria o numero da nota consumidor ou A4"*.
 *
 * ⛔ **O TIPO vai junto, e nao e' enfeite**: medido em 27/08/2026, **243 numeros existem
 * nas DUAS series** (NF-e de 2 a 267, cupom de 4 a 1915) — duas notas diferentes com o
 * mesmo numero, e a coluna afirmaria que sao a mesma. Ele pediu UMA coluna (nao duas), e
 * o tipo cabe nela: numero em cima, serie embaixo.
 *
 * ⚠️ **O vocabulario e' o DELE e o da tela do caixa** — "A4" e "Consumidor", nao "NF-e" e
 * "NFC-e": a coluna TIPO NF da fila de finalizar ja fala assim, e trocar de dialeto entre
 * duas telas do mesmo fluxo obriga a traduzir de cabeca.
 *
 * ⚠️ `no-underline` + `font-bold` no numero pela regra da casa: o iOS Safari transforma
 * codigo numerico curto em link de telefone.
 */
function CelulaNota({ tipo, numero }: { tipo: 'nfe' | 'nfce' | null; numero: number | null }) {
  // Titulo sem nota e' caso REAL e majoritario no legado: 149 dos 390. Traco, nunca vazio
  // — celula em branco le como "faltou carregar", o traco diz "nao tem".
  if (!numero || !tipo) {
    return <td style={{ color: 'var(--text-muted)' }}>—</td>;
  }
  return (
    <td>
      <div className="text-gray-500 font-bold no-underline" style={{ fontSize: 13, lineHeight: 1.15 }}>
        {numero}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', letterSpacing: 0.2 }}>
        {tipo === 'nfe' ? 'A4' : 'Consumidor'}
      </div>
    </td>
  );
}

function toDateInput(v: string | Date | null | undefined): string {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

export function ContasReceberLista() {
  const { usuario } = useAuth();
  const toast = useToast();
  const isAdmin = usuario?.perfil === 'admin';
  const podeCancelar = isAdmin || usuario?.perfil === 'gerente';
  // Baixar titulo (dar quitacao de divida) e estornar baixa sao ADMIN — 2026-08-12, decisao do Ruan
  // depois da apuracao das 3 baixas da Assembleia. Gerente continua podendo cancelar titulo pendente
  // e ocultar do caixa. Demais perfis seguem so-leitura (lista + Extrato + Enviar WhatsApp).
  const podeReceber = isAdmin;

  const [contas, setContas] = useState<ContaReceber[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [filtro, setFiltro] = useState<string>('pendente');
  // `?aba=boletos` vem da faixa "Boletos vencidos" do cabecalho. Efeito (e nao so o estado
  // inicial) porque a faixa pode ser clicada com esta tela ja aberta; o parametro sai da URL
  // depois de aplicado, senao um F5 prenderia a tela nesta aba.
  const [searchParams, setSearchParams] = useSearchParams();
  const abaDaUrl = searchParams.get('aba');
  useEffect(() => {
    if (!abaDaUrl) return;
    if (['pendente', 'vencido', 'pago', 'cancelada', 'todos', 'boletos'].includes(abaDaUrl)) setFiltro(abaDaUrl);
    setSearchParams((p) => { p.delete('aba'); return p; }, { replace: true });
  }, [abaDaUrl, setSearchParams]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);

  const [selecionados, setSelecionados] = useState<Set<number>>(() => new Set());
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const [baixandoNotas, setBaixandoNotas] = useState<number | null>(null);
  const [enviarNotasConta, setEnviarNotasConta] = useState<ContaReceber | null>(null);
  const [itensPorCR, setItensPorCR] = useState<ExtratoData>({});

  const [modalLote, setModalLote] = useState(false);
  const [loteForm, setLoteForm] = useState({ forma_pagamento_id: '', caixa_id: '', observacoes: '', ja_recebido: false });
  const [loteCaixas, setLoteCaixas] = useState<CaixaMin[]>([]);
  const [carregandoCaixas, setCarregandoCaixas] = useState(false);
  const [loteValores, setLoteValores] = useState<Record<number, number>>({});
  const [loteEnviando, setLoteEnviando] = useState(false);
  // Vincular ao recebimento livre (lote) — Pix do MP ou avulso escolhido pra casar.
  const [lotePix, setLotePix] = useState<RecebimentoLivre[] | null>(null);
  const [lotePixSel, setLotePixSel] = useState<RecebimentoLivre | null>(null);
  // "Restante e' acrescimo do cartao" (0320): volta desmarcado a cada troca de recebimento.
  const [loteAcrescimo, setLoteAcrescimo] = useState(false);
  useEffect(() => { setLoteAcrescimo(false); }, [lotePixSel?.origem, lotePixSel?.id]);
  // Cobrança AO VIVO no lote (peça 013/016): cobra o TOTAL do cliente comum; ao pagar, baixa os títulos
  // selecionados sozinho casando o recebimento (o baixar-lote distribui entre eles).
  const [cobrancaLote, setCobrancaLote] = useState<{ tipo: 'pix' | 'cartao'; cardType?: 'credit' | 'debit' } | null>(null);
  const [cobrancaLoteValor, setCobrancaLoteValor] = useState(0);

  // Baixa individual (1 titulo) — modal compacto estilo ControleFC
  const [baixarConta, setBaixarConta] = useState<ContaReceber | null>(null);
  const [baixaForm, setBaixaForm] = useState({ forma_pagamento_id: '', caixa_id: '', valor_pago: 0, observacoes: '', exibir_no_caixa: false, ja_recebido: false });
  const [baixaCaixas, setBaixaCaixas] = useState<CaixaMin[]>([]);
  const [baixaCaixasLoading, setBaixaCaixasLoading] = useState(false);
  const [baixaEnviando, setBaixaEnviando] = useState(false);
  const [salvandoVenc, setSalvandoVenc] = useState(false);
  const [vencEdit, setVencEdit] = useState('');
  // Vincular ao recebimento livre (individual).
  const [baixaPix, setBaixaPix] = useState<RecebimentoLivre[] | null>(null);
  const [baixaPixSel, setBaixaPixSel] = useState<RecebimentoLivre | null>(null);
  const [baixaAcrescimo, setBaixaAcrescimo] = useState(false);
  useEffect(() => { setBaixaAcrescimo(false); }, [baixaPixSel?.origem, baixaPixSel?.id]);
  // Cobrança AO VIVO dentro do Receber Título: quando a forma é Pix/Cartão e o operador clica "Cobrar
  // agora", monta a peça 013/016 que cobra de verdade (QR / maquininha) amarrada ao cliente; ao pagar,
  // casa a baixa do título sozinho com o recebimento gerado (conciliação real, não baixa na fé).
  const [cobrancaAtiva, setCobrancaAtiva] = useState<{ tipo: 'pix' | 'cartao'; cardType?: 'credit' | 'debit' } | null>(null);
  // Maquininha imprime a via do cliente? Desmarcado = sem papel; marca só quando o cliente pede.
  const [imprimirViaCartao, setImprimirViaCartao] = useState(false);
  const [cobrancaValor, setCobrancaValor] = useState(0); // valor cobrado (a calibração do Pix pode subir uns centavos)
  // Provedores de cobrança disponíveis (Pix custo-zero / maquininha Point). Sem eles, a forma cai na
  // baixa na fé de hoje (fallback). Mesmo gate do botão "Cobrar Pix" do caixa.
  const [cobrancaDisp, setCobrancaDisp] = useState({ pix: false, cartao: false });
  // Simulacao de parcelamento (peca 014) — porta PROPRIA, separada da baixa (icone na linha).
  // Abrir a simulacao NUNCA pode encostar em baixar o titulo (era o furo do modal de baixa).
  const [simularTitulo, setSimularTitulo] = useState<ContaReceber | null>(null);
  // Maquininha escolhida na simulacao — muda as parcelas/juros (MP Point x PagBank).
  const [simProvedor, setSimProvedor] = useState<'Mercado Pago' | 'PagBank'>('Mercado Pago');
  // Valor que a loja QUER RECEBER (liquido) — default = valor do titulo, ajustavel. No CR o titulo
  // ja esta fechado (geralmente no pix), entao o operador diz quanto quer liquidar e simula dali.
  const [simValorReceber, setSimValorReceber] = useState(0);
  // Cotacao viva do parcelamento no cartao (Point/MP) do titulo — alimenta a peca 014. So EXIBE:
  // mesmo cano do balcao, sem recalcular. Sem 'cheio' o endpoint assume cheio=liquido (mostra o
  // proprio valor do titulo parcelado, sem markup inventado).
  const [simParcelamento, setSimParcelamento] = useState<EstadoParcelamento>({ loading: false });

  const [cancelarConta, setCancelarConta] = useState<ContaReceber | null>(null);
  // ⚠️ Lista, nao um: o boleto pode cobrir varios titulos ([DA-630]). O icone da linha
  // abre com um; o botao do rodape, com todos os marcados.
  const [boletoContas, setBoletoContas] = useState<ContaReceber[] | null>(null);
  const [estornarConta, setEstornarConta] = useState<ContaReceber | null>(null);
  const [motivoEstorno, setMotivoEstorno] = useState('');
  const [estornando, setEstornando] = useState(false);
  const [historicoConta, setHistoricoConta] = useState<ContaReceber | null>(null);
  const [historicoEventos, setHistoricoEventos] = useState<EventoCR[] | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  const [confirmarExcluir, setConfirmarExcluir] = useState<ContaReceber | null>(null);

  // Aba "Extrato do Cliente" — carrega so quando o usuario clica em Carregar (nada automatico)
  const [extratoCliente, setExtratoCliente] = useState<{ id: number; nome: string; codigo_legado: string | null } | null>(null);
  const [buscaClienteAberta, setBuscaClienteAberta] = useState(false);
  const [extratoCarregado, setExtratoCarregado] = useState(false);

  const [modalExtrato, setModalExtrato] = useState(false);
  const [baixandoExtratoPdf, setBaixandoExtratoPdf] = useState(false);
  const [enviarWhatsAberto, setEnviarWhatsAberto] = useState(false);
  const reciboRef = useRef<ReciboParams | null>(null);
  // Cache das paginas JPG do extrato pra nao regerar a cada destinatario do envio em massa.
  const extratoEnvioRef = useRef<{ paginas: string[]; fileBase: string } | null>(null);
  // Envio do recibo pos-baixa pelo WhatsApp (peca unica de destinatarios). Aberto apos
  // Confirmar Baixa / QR pago. O JPG do recibo e' gerado 1x e cacheado pra todos os contatos.
  const [reciboEnviar, setReciboEnviar] = useState<{ clienteId: number; clienteNome: string; params: ReciboParams } | null>(null);
  const reciboJpgRef = useRef<string | null>(null);

  async function carregar() {
    if (filtro === 'extrato') { setLoading(false); return; } // aba Extrato nao usa a lista de titulos
    setLoading(true);
    try {
      // Aba Boletos: os PENDENTES, recortados no front pelo `boleto_id` que cada linha ja traz.
      const status = filtro === 'boletos' ? 'pendente' : filtro;
      const data = await api.get<ContaReceber[]>('/api/contas-receber', { query: { status: status || undefined } });
      setContas(data);
      setSelecionados(new Set());
      setExpandedIds(new Set());
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function carregarFormas() {
    try {
      const r = await api.get<FormaPagamento[]>('/api/formas-pagamento');
      setFormasPagamento(r.filter((f) => f.tipo !== 'crediario'));
    } catch {}
  }

  useEffect(() => { void carregarFormas(); }, []);
  useEffect(() => { void carregar(); }, [filtro]);

  // Descobre se dá pra cobrar de verdade no Receber Título (Pix custo-zero / maquininha Point). Sem
  // provedor, a forma Pix/Cartão segue na baixa na fé (fallback) — não vira beco sem saída.
  useEffect(() => {
    void Promise.all([
      api.get<{ gratuito?: boolean }>('/api/pix/status').catch(() => ({ gratuito: false })),
      api.get<{ configurada?: boolean }>('/api/cartao/status').catch(() => ({ configurada: false })),
    ]).then(([p, c]) => setCobrancaDisp({ pix: !!p.gratuito, cartao: !!c.configurada }));
  }, []);

  // Cota o parcelamento quando o modal de SIMULACAO esta aberto, pelo VALOR QUE A LOJA QUER RECEBER
  // (simValorReceber) e pela maquininha. Pra a loja LIQUIDAR esse valor, a base do cartao vai ~5%
  // acima (o cliente paga a taxa): liquido = cheio = round(receber / 0.95) faz o calculo devolver
  // base_pix = valor a receber, nas duas adquirentes. MP = /api/cartao/parcelamento; PagBank =
  // /api/caixa/parcelamento (mesmos params). So EXIBE. Debounce 400ms (o operador digita o valor).
  useEffect(() => {
    if (!simularTitulo) { setSimParcelamento({ loading: false }); return; }
    const receberC = Math.round(Number(simValorReceber || 0) * 100);
    if (receberC <= 0) { setSimParcelamento({ loading: false }); return; }
    const baseC = Math.round(receberC / 0.95); // base do cartao: garante base_pix = valor a receber
    let cancelado = false;
    setSimParcelamento({ loading: true });
    const timer = setTimeout(() => {
      const url = simProvedor === 'PagBank'
        ? `/api/caixa/parcelamento?liquido=${baseC}&cheio=${baseC}`
        : `/api/cartao/parcelamento?liquido=${baseC}&cheio=${baseC}`;
      api.get<ResultadoParcelamento>(url)
        .then((r) => { if (!cancelado) setSimParcelamento({ loading: false, data: r }); })
        .catch((e: any) => { if (!cancelado) setSimParcelamento({ loading: false, erro: e?.message }); });
    }, 400);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [simularTitulo?.id, simValorReceber, simProvedor]);

  const buscaLower = busca.trim().toLowerCase();
  const contasFiltradas = contas.filter((c) => {
    if (filtro === 'boletos' && c.boleto_id == null) return false;
    if (!buscaLower) return true;
    return (
      (c.cliente_nome || '').toLowerCase().includes(buscaLower) ||
      (c.orcamento_numero || '').toLowerCase().includes(buscaLower) ||
      (c.cliente_codigo_legado || '') === buscaLower
    );
  });
  // Aba Pagas: a baixa MAIS RECENTE em cima. Ordenar por vencimento (o padrão das outras abas) enterra
  // a baixa de hoje no meio de centenas de títulos antigos — e é a de hoje que se reimprime.
  if (filtro === 'pago') {
    contasFiltradas.sort((a, b) => String(b.data_pagamento ?? '').localeCompare(String(a.data_pagamento ?? '')));
  }
  // Aba Boletos (pedido dele, 08/10/2026): "todos os boletos em aberto por ordem de Vencimento" —
  // o mais antigo em cima, entao o vencido aparece primeiro.
  if (filtro === 'boletos') {
    contasFiltradas.sort((a, b) => String(a.data_vencimento).localeCompare(String(b.data_vencimento)) || a.id - b.id);
  }

  const contasSelecionadas = contasFiltradas.filter((c) => selecionados.has(c.id));
  const totalSelecionado = contasSelecionadas.reduce((s, c) => s + Number(c.valor || 0), 0);
  // ⚠️ **A tela nao oferece o que o servidor recusa** ([DA-593]): a rota nega titulo que
  // nao esta pendente e titulos de clientes diferentes no mesmo papel. Sem esta guarda o
  // botao apareceria e entregaria um erro depois do clique.
  const podeAnexarBoleto = contasSelecionadas.length > 0
    && contasSelecionadas.every((c) => c.status === 'pendente')
    && new Set(contasSelecionadas.map((c) => c.cliente_id)).size === 1;
  const pendentesVisiveis = contasFiltradas.filter((c) => c.status === 'pendente');
  // Na aba Pagas o que se seleciona são os títulos JÁ BAIXADOS — pra reimprimir o recibo daquela baixa.
  // O recibo era um evento (saía uma vez, na hora da baixa) e virou documento recuperável: impressora
  // que falhou, cliente que pediu depois, ou baixa feita pela Conciliação — todos precisam do papel.
  const selecionaveisVisiveis = filtro === 'pago' ? contasFiltradas.filter((c) => c.status === 'pago') : pendentesVisiveis;
  const ehAbaPagas = filtro === 'pago';
  // Na aba Pendentes a coluna Orcamento sai e o espaco vai pro Cliente (pedido dele, 16/10/2026).
  // A busca segue achando pelo numero do orcamento; o titulo de saldo segue marcado no Status
  // ("ja pago R$ X") e no VLR R$ ("de X").
  // Na mesma aba saem tambem VLR PG R$, Pago em e Forma: titulo em aberto nunca tem nada nelas
  // (pedido dele, mesmo dia).
  const ehAbaPendentes = filtro === 'pendente';
  // Vencidas tambem so tem titulo em aberto: as tres colunas de pagamento saem dela (pedido dele,
  // mesmo dia). O Orcamento fica — ele pediu tira-lo so de Pendentes.
  const semColunasDePagamento = ehAbaPendentes || filtro === 'vencido';
  const totalColunas = 16 - (ehAbaPagas ? 1 : 0) - (ehAbaPendentes ? 1 : 0) - (semColunasDePagamento ? 3 : 0);

  // Padrao de grid do sistema: clicar marca a linha, ↑/↓ navegam, Enter/duplo-clique = Receber.
  // So admin/gerente ativam a baixa (onAtivar); os demais perfis navegam em leitura (o Receber e' gated
  // por podeReceber, DA-178). Teclado desligado na aba Extrato, que nao renderiza a lista de titulos.
  const grid = useGridNavegavel<ContaReceber>({
    itens: contasFiltradas,
    getId: (c) => c.id,
    onAtivar: podeReceber ? (c) => abrirReceberUnitario(c) : undefined,
    ativo: filtro !== 'extrato',
  });

  // Troca de aba. Ao entrar na aba Extrato, se a lista atual aponta pra um unico
  // cliente (ex: voce estava filtrando "ALECSANDRO"), ja deixa ele pre-selecionado
  // — mas sem carregar nada: o usuario ainda clica em Carregar.
  function selecionarAba(st: string) {
    setFiltro(st);
    if (st === 'extrato') {
      setExtratoCarregado(false);
      const distintos = new Map<number, { id: number; nome: string; codigo_legado: string | null }>();
      for (const c of contasFiltradas) {
        distintos.set(c.cliente_id, { id: c.cliente_id, nome: c.cliente_nome, codigo_legado: c.cliente_codigo_legado });
      }
      setExtratoCliente(distintos.size === 1 ? [...distintos.values()][0]! : null);
    }
  }

  function toggleSelecionado(id: number) {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleTodos() {
    if (selecionados.size === selecionaveisVisiveis.length && selecionaveisVisiveis.length > 0) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(selecionaveisVisiveis.map((c) => c.id)));
    }
  }

  async function toggleExibirCaixa(c: ContaReceber) {
    try {
      await api.patch(`/api/contas-receber/${c.id}/exibir-caixa`, { exibir: !c.exibir_no_caixa });
      setContas((prev) => prev.map((x) => x.id === c.id ? { ...x, exibir_no_caixa: !x.exibir_no_caixa } : x));
    } catch (e: any) { toast.erro(e.message); }
  }

  async function toggleExpand(crId: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(crId)) {
        next.delete(crId);
      } else {
        next.add(crId);
        if (itensPorCR[crId] === undefined) {
          api.get<ExtratoData>('/api/contas-receber/extrato-itens', { query: { ids: String(crId) } })
            .then((data) => setItensPorCR((p) => ({ ...p, ...data })))
            .catch(() => setItensPorCR((p) => ({ ...p, [crId]: { comprador: null, itens: [] } })));
        }
      }
      return next;
    });
  }

  function abrirModalLote() {
    if (contasSelecionadas.length === 0) return;
    const vals: Record<number, number> = {};
    contasSelecionadas.forEach((c) => { vals[c.id] = Number(c.valor); });
    setLoteValores(vals);
    // Forma comeca VAZIA (escolha consciente — fim do clique cego, DA-221). Sem auto-localStorage.
    setLoteForm({ forma_pagamento_id: '', caixa_id: '', observacoes: '', ja_recebido: false });
    setLotePix(null);
    setLotePixSel(null);
    setCobrancaLote(null);
    setModalLote(true);
  }

  function abrirReceberUnitario(c: ContaReceber) {
    if (c.status !== 'pendente') return;
    grid.setSelectedId(c.id); // mantem a linha destacada ao voltar do modal
    // Forma comeca VAZIA — exige olhar a conta antes de confirmar (DA-221).
    setBaixaForm({
      forma_pagamento_id: '',
      caixa_id: '',
      valor_pago: Number(c.valor),
      observacoes: '',
      exibir_no_caixa: !!c.exibir_no_caixa,
      ja_recebido: false,
    });
    setBaixaPix(null);
    setBaixaPixSel(null);
    setCobrancaAtiva(null);
    setVencEdit(toDateInput(c.data_vencimento));
    setBaixarConta(c);
  }

  // Carrega caixas que aceitam a forma escolhida no modal single
  useEffect(() => {
    // Trocar a forma zera o recebimento CASADO e a cobrança — mas NAO a LISTA de recebimentos livres:
    // ela nao depende da forma (o backend devolve Pix do MP + avulsos do cliente sempre), e quem a
    // recarrega e o efeito abaixo, que so escuta o VALOR. Zerando aqui, escolher a forma apagava o
    // bloco "Vincular a um recebimento livre" pra sempre — e como o valor ja nasce preenchido com o
    // do titulo, ninguem mexia nele e o bloco nunca voltava. Ver [DA-593].
    setBaixaPixSel(null); setCobrancaAtiva(null);
    if (!baixarConta || !baixaForm.forma_pagamento_id) { setBaixaCaixas([]); return; }
    const ehPix = ehFormaPix(formasPagamento, baixaForm.forma_pagamento_id);
    setBaixaCaixasLoading(true);
    api.get<CaixaMin[]>(`/api/caixa/contas-por-forma/${baixaForm.forma_pagamento_id}`)
      .then((r) => {
        setBaixaCaixas(r);
        const last = localStorage.getItem(KEY_LAST_CAIXA);
        const mp = r.find((c) => /mercado\s*pago/i.test(c.nome));
        const bb = r.find((c) => /banco\s*do\s*brasil/i.test(c.nome));
        // Pix cai no Mercado Pago — pré-aponta pra lá (é onde o vínculo acontece).
        if (ehPix && mp) {
          setBaixaForm((p) => ({ ...p, caixa_id: String(mp.id) }));
        } else if (bb) {
          setBaixaForm((p) => ({ ...p, caixa_id: String(bb.id) }));
        } else if (last && r.some((c) => String(c.id) === last)) {
          setBaixaForm((p) => ({ ...p, caixa_id: last }));
        } else if (r.length === 1) {
          setBaixaForm((p) => ({ ...p, caixa_id: String(r[0]!.id) }));
        } else {
          setBaixaForm((p) => ({ ...p, caixa_id: '' }));
        }
      })
      .catch(() => setBaixaCaixas([]))
      .finally(() => setBaixaCaixasLoading(false));
  }, [baixarConta, baixaForm.forma_pagamento_id]);

  // Carrega os recebimentos LIVRES do cliente (Pix do MP + avulsos dinheiro/cartão) quando o modal de
  // baixa está aberto. O operador pode casar a baixa com um recebimento que já entrou (sem dobrar o
  // caixa). O Pix do MP só vincula com forma=Pix + caixa=Mercado Pago (o backend reforça no confirmar).
  useEffect(() => {
    if (!baixarConta) { setBaixaPix(null); setBaixaPixSel(null); return; }
    api.get<RecebimentoLivre[]>('/api/contas-receber/recebimentos-livres', {
      query: { cliente_id: String(baixarConta.cliente_id), valor: String(baixaForm.valor_pago || '') },
    })
      .then((lista) => {
        setBaixaPix(lista);
        // Preserva a escolha ao mexer no valor (só limpa se o recebimento sumir) — pro "Ajustar" não desmarcar.
        setBaixaPixSel((sel) => (sel && lista.some((p) => p.origem === sel.origem && p.id === sel.id) ? sel : null));
      })
      .catch(() => setBaixaPix([]));
  }, [baixarConta, baixaForm.valor_pago]);

  // Edita a data de vencimento do titulo pendente no modal (admin/gerente). O campo so' guarda o
  // rascunho; quem grava e' o botao Salvar — ou o Confirmar Baixa/Cobrar, que recolhe a data
  // pendente antes de seguir (campo preenchido nao pode depender de um segundo clique). Antes
  // gravava a cada mudanca do campo, e o Ruan achou "nada intuitivo" (13/10/2026).
  // Mantem o formato '...T00:00:00.000Z' que o backend devolve pra formatDataSP nao deslocar o dia.
  const vencPendente = !!baixarConta && !!vencEdit && vencEdit !== toDateInput(baixarConta.data_vencimento);
  async function salvarVencimento(): Promise<boolean> {
    if (!baixarConta || !vencPendente) return true;
    const novaIso = `${vencEdit}T00:00:00.000Z`;
    setSalvandoVenc(true);
    try {
      await api.patch(`/api/contas-receber/${baixarConta.id}/vencimento`, { data_vencimento: vencEdit });
      setBaixarConta((p) => (p ? { ...p, data_vencimento: novaIso } : p));
      setContas((prev) => prev.map((x) => (x.id === baixarConta.id ? { ...x, data_vencimento: novaIso } : x)));
      toast.sucesso('Vencimento atualizado');
      return true;
    } catch (e: any) {
      toast.erro(e.message);
      return false;
    } finally {
      setSalvandoVenc(false);
    }
  }

  // A lista de vinculo obedece a CONTA escolhida: baixa na Cora so pode casar com dinheiro que caiu na
  // Cora — e como o sistema nao le a Cora, isso e NENHUM. Oferecer o Pix do Mercado Pago numa baixa da
  // Cora seria propor um vinculo que o backend RECUSA no confirmar, e afirmar na tela que o dinheiro
  // entrou onde nao entrou. Sem conta escolhida, mostra tudo — dai escolher o recebimento e que define
  // a forma e a conta (o atalho mais curto). A FORMA nao filtra: ela vem do recebimento. Ver [DA-593].
  const baixaLivresDaConta = useMemo(() => {
    if (!baixaPix) return null;
    const caixaId = Number(baixaForm.caixa_id) || null;
    return caixaId ? baixaPix.filter((r) => Number(r.caixa_id) === caixaId) : baixaPix;
  }, [baixaPix, baixaForm.caixa_id]);

  // Regra UNICA do "oferece cobrar ao vivo" (peça 013/016) — lida pelo checkbox "Já recebido", pelo
  // botão do rodapé e pela mensagem de vazio do seletor. Escrita em três lugares, divergia no primeiro
  // ajuste. Cartão só cobra ao vivo pela Point (Mercado Pago); PagBank/outros são manuais.
  const ofereceCobrarSingle = useMemo(() => {
    const tipo = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.tipo ?? null;
    const caixaSel = baixaCaixas.find((c) => String(c.id) === baixaForm.caixa_id);
    const ehCaixaMP = !!caixaSel && /mercado\s*pago/i.test(caixaSel.nome);
    return !baixaPixSel && ((tipo === 'pix' && cobrancaDisp.pix)
      || ((tipo === 'cartao_credito' || tipo === 'cartao_debito') && cobrancaDisp.cartao && ehCaixaMP));
  }, [formasPagamento, baixaForm.forma_pagamento_id, baixaForm.caixa_id, baixaCaixas, baixaPixSel, cobrancaDisp]);

  // Escolher o recebimento JÁ ajusta o valor pago: capa no disponível do recebimento (o resto do título
  // vira saldo em aberto). Automático no clique — não faz sentido seguir podendo baixar além dele.
  function escolherRecebimentoSingle(p: RecebimentoLivre | null) {
    setBaixaPixSel(p);
    if (p && baixarConta) {
      setBaixaForm((f) => ({ ...f, valor_pago: Math.min(Number(baixarConta.valor), Number(p.disponivel)) }));
    }
  }

  // O recibo e da BAIXA, nao do titulo: quem pagou 14 titulos de uma vez quer UM papel com os 14, e
  // nao vai cacar os 14 no meio dos 118 ja pagos do cliente. Este botao (impressora, na linha paga)
  // reimprime o recibo do LOTE INTEIRO daquela baixa — os titulos irmaos sao os do mesmo cliente,
  // baixados no mesmo dia, na mesma forma. (O schema nao guarda um "id da baixa"; dia+cliente+forma e
  // o agrupamento fiel que existe. Duas baixas iguais no mesmo dia sairiam num recibo so.)
  function reciboDaBaixa(c: ContaReceber) {
    montarEImprimirRecibo(irmaosDaBaixa(c));
  }

  // Os titulos que foram baixados JUNTO com este — mesmo cliente, mesmo INSTANTE de baixa.
  // ⛔ Nao e' mais "mesmo dia + mesma forma" ([DA-654]): era a FORMA no criterio que partia o
  // recibo de quem pagou em Pix + Dinheiro, entregando um papel que so podia contar metade.
  // E nao e' o dia inteiro tambem — manha e tarde sao dois pagamentos, logo dois recibos.
  function irmaosDaBaixa(c: ContaReceber): ContaReceber[] {
    const chave = chaveDoRecibo(c);
    const irmaos = contas.filter((x) => x.status === 'pago' && chaveDoRecibo(x) === chave);
    return irmaos.length > 0 ? irmaos : [c];
  }

  // O cliente que quita varios titulos pede "as notas" — todas, de uma vez. Baixa UM PDF com todas as
  // DANFEs da baixa (uma por pagina). PDF e nao .zip porque o destino e o WhatsApp do cliente, e a API
  // da Meta RECUSA zip — arquivo que o operador nao consegue enviar nao e entrega. PDF, nunca XML: o
  // XML e do contador; o cliente quer o documento.
  async function notasDaBaixa(c: ContaReceber) {
    const irmaos = irmaosDaBaixa(c);
    setBaixandoNotas(c.id);
    try {
      const token = getToken();
      const r = await fetch('/api/nfe/danfes-lote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ conta_receber_ids: irmaos.map((x) => x.id) }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ erro: String(r.status) })) as { erro?: string };
        throw new Error(j.erro || 'falha ao gerar o arquivo');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const quando = (c.data_pagamento ?? '').slice(0, 10).split('-').reverse().join('-');
      a.download = `Notas ${c.cliente_nome} ${quando}.pdf`.replace(/[\\/:*?"<>|]/g, '');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = r.headers.get('X-Notas-Total') ?? '';
      const faltando = r.headers.get('X-Notas-Faltando');
      toast.sucesso(`${total} nota(s) num PDF so`);
      // Nota faltando nao some calada: quem entrega precisa saber que o pacote esta incompleto.
      if (faltando) toast.erro(`Sem NF-e autorizada (ficaram de fora): ${faltando}`);
    } catch (e: any) { toast.erro(e.message); }
    finally { setBaixandoNotas(null); }
  }

  // Reimprime o recibo de titulos JA PAGOS (aba Pagas). Nada vem de memoria: remonta o documento a
  // partir do que ficou gravado no titulo (valor pago, data da baixa, forma, operador) — por isso
  // funciona pra qualquer baixa, inclusive as feitas pela Conciliacao Bancaria ou meses atras.
  // Um recibo = um cliente (o papel tem o nome dele no topo).
  function reimprimirRecibo() {
    const pagos = contasSelecionadas.filter((c) => c.status === 'pago');
    if (pagos.length === 0) { toast.erro('Selecione titulos ja pagos'); return; }
    montarEImprimirRecibo(pagos);
  }

  function montarEImprimirRecibo(pagos: ContaReceber[]) {
    const clientes = new Set(pagos.map((c) => c.cliente_id));
    if (clientes.size > 1) { toast.erro('Selecione titulos de um cliente so — o recibo e nominal'); return; }
    const primeiro = pagos[0]!;
    // ⚠️ Data da baixa pelo `formatDataSP`, nunca `new Date(...)`: `data_pagamento` e' `date` pura
    // e o construtor a le como meia-noite UTC — o papel saia com o DIA ANTERIOR (medido em
    // 01/09/2026: baixa de 19/08 impressa como 18/08). E **sem hora**: ela nao existe no titulo,
    // e o "21:00" que aparecia era o efeito do mesmo desvio, nao um horario medido. A hora real
    // da baixa vive na trilha `contas_receber_eventos` ([DA-514]), que o botao Historico mostra.
    imprimirHTML(gerarReciboHTML({
      cliente: primeiro.cliente_nome,
      cliente_cpf_cnpj: primeiro.cliente_cpf_cnpj,
      formaPagamento: primeiro.forma_pagamento_nome || '',
      operador: primeiro.operador_baixa_nome || '',
      data: primeiro.data_pagamento ? formatDataSP(primeiro.data_pagamento) : formatDataSP(new Date()),
      titulos: pagos.map((c) => {
        // ⚠️ `valor_original` no titulo de SALDO carrega o valor do titulo PAI (139 num saldo de
        // 73), entao usa-lo como "Original" da linha imprimiria um saldo de 73 como se fossem 139
        // e o papel deixaria de fechar. No saldo, o original da linha e' o proprio valor.
        const ehSaldo = ehTituloDeSaldo(c);
        const original = ehSaldo ? Number(c.valor) : Number(c.valor_original ?? c.valor);
        const pago = Number(c.valor_pago ?? c.valor);
        return {
          orcamento_numero: c.orcamento_numero,
          nota_tipo: c.nota_tipo,
          nota_numero: c.nota_numero,
          data_emissao: c.data_emissao,
          data_vencimento: c.data_vencimento,
          valor_original: original,
          // Pago = o que o CLIENTE pagou (com acrescimo do cartao, 0321); o saldo segue pelo
          // valor_pago do titulo, senao o acrescimo viraria saldo negativo.
          valor_pago: valorPagoCliente(c),
          saldo: +(original - pago).toFixed(2),
          forma_pagamento: c.forma_pagamento_nome,
          eh_saldo: ehSaldo,
          conta_receber_id: c.id,
        };
      }),
      total_recebido: +pagos.reduce((s, c) => s + valorPagoCliente(c), 0).toFixed(2),
      observacoes: null,
    }));
  }

  // Monta o recibo de UM titulo recem-baixado, pra oferecer o envio pelo WhatsApp pos-baixa.
  function montarReciboSingle(conta: ContaReceber, valorPago: number, formaNome: string, saldoGerado: { id: number; valor: number; data_vencimento: string } | null, obs?: string | null, acrescimo = 0): ReciboParams {
    // Mesma armadilha do recibo em lote: num titulo de SALDO o `valor_original` e' o do PAI, e
    // usa-lo como "Original" da linha imprime 139 num titulo de 73 ([DA-654]).
    const original = ehTituloDeSaldo(conta) ? Number(conta.valor) : Number(conta.valor_original ?? conta.valor);
    return {
      cliente: conta.cliente_nome,
      cliente_cpf_cnpj: conta.cliente_cpf_cnpj,
      formaPagamento: formaNome,
      operador: usuario?.nome || '',
      data: new Date().toLocaleDateString('pt-BR'),
      hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      titulos: [{
        orcamento_numero: conta.orcamento_numero,
        nota_tipo: conta.nota_tipo,
        nota_numero: conta.nota_numero,
        data_emissao: conta.data_emissao,
        data_vencimento: conta.data_vencimento,
        valor_original: original,
        // Pago = o que o cliente pagou (titulo + acrescimo do cartao); o saldo segue pelo titulo.
        valor_pago: +(valorPago + acrescimo).toFixed(2),
        saldo: +(original - valorPago).toFixed(2),
        forma_pagamento: formaNome || null,
        eh_saldo: ehTituloDeSaldo(conta),
        conta_receber_id: conta.id,
      }],
      saldos_gerados: saldoGerado ? [saldoGerado] : undefined,
      total_recebido: +(valorPago + acrescimo).toFixed(2),
      observacoes: obs || null,
    };
  }

  async function confirmarBaixaSingle() {
    if (!baixarConta) return;
    const conta = baixarConta;
    // Com recebimento casado, forma/caixa vêm DELE (o backend deriva); só a baixa manual exige escolher.
    if (!baixaPixSel) {
      if (!baixaForm.forma_pagamento_id) { toast.erro('Selecione forma de pagamento'); return; }
      if (!baixaForm.caixa_id) { toast.erro('Selecione o caixa destino'); return; }
    }
    const vp = baixaForm.valor_pago;
    if (!vp || vp <= 0) { toast.erro('Informe o valor pago'); return; }
    if (vp > Number(baixarConta.valor) + 0.005) { toast.erro('Valor pago nao pode ser maior que o valor da conta'); return; }
    if (!(await salvarVencimento())) return;

    setBaixaEnviando(true);
    try {
      const r = await api.post<{ conta: any; saldo_gerado: any; pix_vinculo: any; avulso_vinculo: any }>(`/api/contas-receber/${baixarConta.id}/baixar`, {
        // Casou com recebimento → manda forma/caixa DELE (o backend é a fonte da verdade e re-deriva).
        forma_pagamento_id: baixaPixSel ? (baixaPixSel.forma_pagamento_id ?? undefined) : Number(baixaForm.forma_pagamento_id),
        caixa_id: baixaPixSel ? (baixaPixSel.caixa_id ?? undefined) : Number(baixaForm.caixa_id),
        valor_pago: vp,
        observacoes: baixaForm.observacoes || undefined,
        exibir_no_caixa: baixaForm.exibir_no_caixa,
        mp_recebimento_id: baixaPixSel?.origem === 'pix' ? baixaPixSel.id : undefined,
        recebimento_avulso_id: baixaPixSel?.origem === 'avulso' ? baixaPixSel.id : undefined,
        acrescimo_restante: baixaAcrescimo && restanteAcrescimoCartao(baixaPixSel, vp) > 0 ? true : undefined,
      });
      localStorage.setItem(KEY_LAST_CAIXA, baixaForm.caixa_id);
      const vinc = r.pix_vinculo ?? r.avulso_vinculo;
      const sufVinc = vinc ? (vinc.parcial ? ' — recebimento vinculado (parcial)' : ' — recebimento vinculado') : '';
      toast.sucesso((r.saldo_gerado ? `Recebido. Saldo de ${formatMoeda(r.saldo_gerado.valor)} gerado` : 'Conta baixada') + sufVinc);
      // Oferece enviar o recibo pelo WhatsApp (pos Confirmar Baixa).
      const formaNome = baixaPixSel
        ? (formasPagamento.find((f) => f.id === baixaPixSel!.forma_pagamento_id)?.nome || 'Recebimento')
        : (formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.nome || '');
      reciboJpgRef.current = null;
      setReciboEnviar({ clienteId: conta.cliente_id, clienteNome: conta.cliente_nome, params: montarReciboSingle(conta, vp, formaNome, r.saldo_gerado ?? null, baixaForm.observacoes, Number(r.avulso_vinculo?.acrescimo ?? 0)) });
      setBaixarConta(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setBaixaEnviando(false);
    }
  }

  // Liga a cobrança AO VIVO (peça 013/016) a partir da forma escolhida + valor. O título baixa sozinho
  // quando o cliente pagar (ver baixarTituloComRecebimento). Conciliação real, não baixa na fé.
  async function iniciarCobranca() {
    if (!baixarConta) return;
    const tipo = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.tipo ?? null;
    const vp = baixaForm.valor_pago;
    if (!vp || vp <= 0) { toast.erro('Informe o valor pago'); return; }
    if (vp > Number(baixarConta.valor) + 0.005) { toast.erro('O valor não pode passar do valor da conta'); return; }
    if (!(await salvarVencimento())) return;
    setCobrancaValor(vp);
    if (tipo === 'pix') setCobrancaAtiva({ tipo: 'pix' });
    else if (tipo === 'cartao_credito' || tipo === 'cartao_debito') {
      setCobrancaAtiva({ tipo: 'cartao', cardType: tipo === 'cartao_debito' ? 'debit' : 'credit' });
    }
  }

  // Cobrança paga → baixa o título casando o recebimento gerado (mp_recebimento_id pro Pix,
  // recebimento_avulso_id pro cartão). É o MESMO endpoint da baixa manual com recebimento casado:
  // deriva forma/caixa do recebimento, não duplica suprimento, cria o vínculo e trata o parcial.
  async function baixarTituloComRecebimento(origem: 'pix' | 'avulso', recebimentoId: number | null) {
    if (!baixarConta) return;
    const conta = baixarConta;
    if (recebimentoId == null) {
      // Pagou mas não veio o id (raro): o dinheiro ficou LIVRE — degrada pro fluxo de hoje (casar na mão).
      toast.sucesso('Pagamento recebido — ficou livre. Vincule pelo recebimento livre abaixo.');
      setCobrancaAtiva(null);
      try {
        const lista = await api.get<RecebimentoLivre[]>('/api/contas-receber/recebimentos-livres', {
          query: { cliente_id: String(baixarConta.cliente_id), valor: String(baixaForm.valor_pago || '') },
        });
        setBaixaPix(lista);
      } catch { /* a baixa segue manual */ }
      return;
    }
    setBaixaEnviando(true);
    try {
      const r = await api.post<{ saldo_gerado: any }>(`/api/contas-receber/${baixarConta.id}/baixar`, {
        valor_pago: baixaForm.valor_pago,
        observacoes: baixaForm.observacoes || undefined,
        exibir_no_caixa: baixaForm.exibir_no_caixa,
        mp_recebimento_id: origem === 'pix' ? recebimentoId : undefined,
        recebimento_avulso_id: origem === 'avulso' ? recebimentoId : undefined,
      });
      toast.sucesso(r.saldo_gerado
        ? `Recebido. Saldo de ${formatMoeda(r.saldo_gerado.valor)} gerado — título conciliado`
        : 'Título baixado e conciliado');
      // Oferece enviar o recibo pelo WhatsApp (apos o QR pago).
      const formaNome = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.nome
        || (origem === 'pix' ? 'Pix' : 'Cartao');
      reciboJpgRef.current = null;
      setReciboEnviar({ clienteId: conta.cliente_id, clienteNome: conta.cliente_nome, params: montarReciboSingle(conta, baixaForm.valor_pago, formaNome, (r as { saldo_gerado: { id: number; valor: number; data_vencimento: string } | null }).saldo_gerado ?? null, baixaForm.observacoes) });
      setCobrancaAtiva(null);
      setBaixarConta(null);
      void carregar();
    } catch (e: any) {
      // A cobrança PAGOU mas a baixa não fechou: o recebimento ficou livre (sem perda de dinheiro). Avisa.
      toast.erro('Pagamento recebido, mas a baixa não fechou: ' + e.message + '. O valor ficou livre pra vincular.');
    } finally {
      setBaixaEnviando(false);
    }
  }

  // Carrega caixas que aceitam a forma escolhida.
  useEffect(() => {
    // Mesma correcao do modal individual: a lista de recebimentos livres nao depende da forma. Ver [DA-593].
    setLotePixSel(null); setCobrancaLote(null);
    if (!modalLote || !loteForm.forma_pagamento_id) { setLoteCaixas([]); return; }
    const ehPix = ehFormaPix(formasPagamento, loteForm.forma_pagamento_id);
    setCarregandoCaixas(true);
    api.get<CaixaMin[]>(`/api/caixa/contas-por-forma/${loteForm.forma_pagamento_id}`)
      .then((r) => {
        setLoteCaixas(r);
        const last = localStorage.getItem(KEY_LAST_CAIXA);
        const mp = r.find((c) => /mercado\s*pago/i.test(c.nome));
        // Pix cai no Mercado Pago — pré-aponta pra lá (é onde o vínculo acontece).
        if (ehPix && mp) {
          setLoteForm((p) => ({ ...p, caixa_id: String(mp.id) }));
        } else if (last && r.some((c) => String(c.id) === last)) {
          setLoteForm((p) => ({ ...p, caixa_id: last }));
        } else if (r.length === 1) {
          setLoteForm((p) => ({ ...p, caixa_id: String(r[0]!.id) }));
        } else {
          setLoteForm((p) => ({ ...p, caixa_id: '' }));
        }
      })
      .catch(() => setLoteCaixas([]))
      .finally(() => setCarregandoCaixas(false));
  }, [modalLote, loteForm.forma_pagamento_id]);

  const totalLote = useMemo(() => {
    return contasSelecionadas.reduce((s, c) => s + (loteValores[c.id] || 0), 0);
  }, [contasSelecionadas, loteValores]);

  // Cliente comum do lote (avulso é por-cliente; só oferece avulso quando todos compartilham 1 cliente).
  const clienteComumLote = useMemo(() => {
    const ids = new Set(contasSelecionadas.map((c) => c.cliente_id));
    return ids.size === 1 ? [...ids][0]! : null;
  }, [contasSelecionadas]);

  // Mesma regra do individual: a lista de vinculo obedece a CONTA escolhida. Ver [DA-593].
  const loteLivresDaConta = useMemo(() => {
    if (!lotePix) return null;
    const caixaId = Number(loteForm.caixa_id) || null;
    return caixaId ? lotePix.filter((r) => Number(r.caixa_id) === caixaId) : lotePix;
  }, [lotePix, loteForm.caixa_id]);

  // Regra UNICA do "oferece cobrar ao vivo" no lote (o individual tem a irmã acima). A cobrança é por
  // CLIENTE, então o lote só oferece quando os títulos compartilham um cliente e há valor.
  const ofereceCobrarLote = useMemo(() => {
    const tipo = formasPagamento.find((f) => String(f.id) === loteForm.forma_pagamento_id)?.tipo ?? null;
    const caixaSel = loteCaixas.find((c) => String(c.id) === loteForm.caixa_id);
    const ehCaixaMP = !!caixaSel && /mercado\s*pago/i.test(caixaSel.nome);
    return !lotePixSel && !!clienteComumLote && totalLote > 0
      && ((tipo === 'pix' && cobrancaDisp.pix)
        || ((tipo === 'cartao_credito' || tipo === 'cartao_debito') && cobrancaDisp.cartao && ehCaixaMP));
  }, [formasPagamento, loteForm.forma_pagamento_id, loteForm.caixa_id, loteCaixas, lotePixSel, clienteComumLote, totalLote, cobrancaDisp]);

  // Carrega os recebimentos LIVRES disponíveis (lote). Pix do MP + avulsos do cliente comum (se houver).
  // PRESERVA a escolha ao mexer nos valores (só limpa se o recebimento sumir da lista) — assim o
  // "Ajustar pro recebimento" e a edição dos "Pagar" não desmarcam a forma/caixa travados.
  useEffect(() => {
    if (!modalLote) { setLotePix(null); setLotePixSel(null); return; }
    const query: Record<string, string> = { valor: String(totalLote || '') };
    if (clienteComumLote) query.cliente_id = String(clienteComumLote);
    api.get<RecebimentoLivre[]>('/api/contas-receber/recebimentos-livres', { query })
      .then((lista) => {
        setLotePix(lista);
        setLotePixSel((sel) => (sel && lista.some((p) => p.origem === sel.origem && p.id === sel.id) ? sel : null));
      })
      .catch(() => setLotePix([]));
  }, [modalLote, totalLote, clienteComumLote]);

  // rec (opcional) = recebimento gerado por uma cobrança ao vivo (peça 013/016) — quando vem, casa a
  // baixa do lote com ele igual ao "vincular recebimento livre"; o backend distribui pelos títulos.
  async function confirmarLote(rec?: { origem: 'pix' | 'avulso'; recebimentoId: number }) {
    const recCasado = rec
      ? { origem: rec.origem, id: rec.recebimentoId }
      : (lotePixSel ? { origem: lotePixSel.origem, id: lotePixSel.id } : null);
    // Com recebimento casado, forma/caixa vêm DELE (o backend deriva); só a baixa manual exige escolher.
    if (!recCasado) {
      if (!loteForm.forma_pagamento_id) { toast.erro('Selecione forma de pagamento'); return; }
      if (!loteForm.caixa_id) { toast.erro('Selecione o caixa de origem'); return; }
    }
    const titulos = contasSelecionadas
      .map((c) => ({ id: c.id, valor_pago: loteValores[c.id] || 0 }))
      .filter((t) => t.valor_pago > 0);
    if (titulos.length === 0) { toast.erro('Nenhum valor informado'); return; }

    setLoteEnviando(true);
    try {
      const r = await api.post<{ baixados: any[]; saldos_gerados: any[]; total_recebido: number; pix_vinculo: any; avulso_vinculo: any }>('/api/contas-receber/baixar-lote', {
        titulos,
        // Casou com recebimento → o backend é a fonte da verdade e re-deriva forma/caixa dele.
        forma_pagamento_id: recCasado ? undefined : Number(loteForm.forma_pagamento_id),
        caixa_id: recCasado ? undefined : Number(loteForm.caixa_id),
        observacoes: loteForm.observacoes || undefined,
        mp_recebimento_id: recCasado?.origem === 'pix' ? recCasado.id : undefined,
        recebimento_avulso_id: recCasado?.origem === 'avulso' ? recCasado.id : undefined,
        // So vale pro recebimento ESCOLHIDO na lista (nao pro gerado por cobranca ao vivo).
        acrescimo_restante: !rec && loteAcrescimo && restanteAcrescimoCartao(lotePixSel, totalLote) > 0 ? true : undefined,
      });
      if (loteForm.caixa_id) localStorage.setItem(KEY_LAST_CAIXA, loteForm.caixa_id);
      const formaIdReciboLote = lotePixSel ? lotePixSel.forma_pagamento_id : Number(loteForm.forma_pagamento_id);
      const fpNome = formasPagamento.find((f) => f.id === Number(formaIdReciboLote))?.nome || '';
      const cliente = contasSelecionadas[0]?.cliente_nome || '';
      const cpf = contasSelecionadas[0]?.cliente_cpf_cnpj || '';

      reciboRef.current = {
        cliente,
        cliente_cpf_cnpj: cpf,
        formaPagamento: fpNome,
        operador: usuario?.nome || '',
        data: new Date().toLocaleDateString('pt-BR'),
        hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        titulos: r.baixados.map((b) => {
          const orig = contasSelecionadas.find((c) => c.id === b.id);
          return {
            orcamento_numero: orig?.orcamento_numero || null,
            nota_tipo: orig?.nota_tipo ?? null,
            nota_numero: orig?.nota_numero ?? null,
            data_emissao: orig?.data_emissao || null,
            data_vencimento: b.data_vencimento,
            valor_original: Number(b.valor_original),
            // O que o cliente pagou (com acrescimo do cartao, 0321); o saldo segue pelo titulo.
            valor_pago: Number(b.valor_pago_cliente ?? b.valor_pago),
            saldo: +(Number(b.valor_original) - Number(b.valor_pago)).toFixed(2),
            // Uma baixa = uma forma, entao aqui a coluna Forma nao chega a aparecer (o gerador
            // so a liga com duas ou mais) — mas ela vai preenchida pro papel nao depender de
            // quem o montou, e o id vai pro cruzamento com os saldos gerados ([DA-654]).
            forma_pagamento: fpNome || null,
            eh_saldo: orig ? ehTituloDeSaldo(orig) : false,
            conta_receber_id: b.id,
          };
        }),
        saldos_gerados: r.saldos_gerados,
        total_recebido: +(Number(r.total_recebido) + Number(r.avulso_vinculo?.acrescimo ?? 0)).toFixed(2),
        observacoes: loteForm.observacoes || null,
      };

      const vinc = r.pix_vinculo ?? r.avulso_vinculo;
      const sufVinc = vinc ? (vinc.parcial ? ' — recebimento vinculado (parcial)' : ' — recebimento vinculado') : '';
      toast.sucesso(`${r.baixados.length} titulo(s) baixado(s)` + sufVinc);
      setModalLote(false);
      void carregar();
      // Mostra recibo automaticamente
      setTimeout(() => {
        if (reciboRef.current) imprimirHTML(gerarReciboHTML(reciboRef.current));
      }, 200);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoteEnviando(false);
    }
  }

  // Escolher o recebimento JÁ ajusta os valores: distribui o disponível pelos títulos (na ordem da lista),
  // pagando cheio até o dinheiro acabar; o que sobra fica com "Pagar" reduzido → vira saldo em aberto.
  // Automático no clique (escolher o recebimento = usar o valor dele); não tem por que seguir podendo
  // baixar além. Ver [[feedback_baixa_dinheiro_conta_visivel]].
  function escolherRecebimentoLote(p: RecebimentoLivre | null) {
    setLotePixSel(p);
    if (!p) return;
    let rem = Number(p.disponivel);
    const novos: Record<number, number> = {};
    for (const c of contasSelecionadas) {
      const v = Math.max(0, Math.min(Number(c.valor), +rem.toFixed(2)));
      novos[c.id] = +v.toFixed(2);
      rem = +(rem - v).toFixed(2);
    }
    setLoteValores(novos);
  }

  // Liga a cobrança AO VIVO do lote (cobra o TOTAL do cliente comum). Exige 1 cliente só nos títulos
  // selecionados (a cobrança é por cliente) — a forma já garante isso no gate do botão.
  function iniciarCobrancaLote() {
    if (!clienteComumLote || totalLote <= 0) { toast.erro('Selecione títulos de um único cliente com valor a cobrar'); return; }
    const tipo = formasPagamento.find((f) => String(f.id) === loteForm.forma_pagamento_id)?.tipo ?? null;
    setCobrancaLoteValor(totalLote);
    if (tipo === 'pix') setCobrancaLote({ tipo: 'pix' });
    else if (tipo === 'cartao_credito' || tipo === 'cartao_debito') {
      setCobrancaLote({ tipo: 'cartao', cardType: tipo === 'cartao_debito' ? 'debit' : 'credit' });
    }
  }

  // Cobrança do lote paga → baixa os títulos casando o recebimento (o backend distribui entre eles).
  async function baixarLoteComRecebimento(origem: 'pix' | 'avulso', recebimentoId: number | null) {
    if (recebimentoId == null) {
      // Pagou mas não veio o id (raro): ficou LIVRE — degrada pro fluxo de hoje (casar na mão).
      toast.sucesso('Pagamento recebido — ficou livre. Vincule pelo recebimento livre.');
      setCobrancaLote(null);
      return;
    }
    setCobrancaLote(null);
    await confirmarLote({ origem, recebimentoId });
  }

  async function confirmarCancelamento() {
    if (!cancelarConta) return;
    if (motivoCancelamento.trim().length < 3) { toast.erro('Informe o motivo'); return; }
    try {
      await api.post(`/api/contas-receber/${cancelarConta.id}/cancelar`, { motivo: motivoCancelamento });
      toast.sucesso('conta cancelada');
      setCancelarConta(null);
      setMotivoCancelamento('');
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  async function confirmarEstorno() {
    if (!estornarConta) return;
    if (motivoEstorno.trim().length < 3) { toast.erro('Informe o motivo do estorno'); return; }
    setEstornando(true);
    try {
      const r: any = await api.post(`/api/contas-receber/${estornarConta.id}/estornar`, { motivo: motivoEstorno });
      toast.sucesso(r?.contrapartida
        ? `Titulo em aberto — saiu ${formatMoeda(r.contrapartida.valor)} do caixa como contrapartida`
        : 'Titulo voltou pra em aberto');
      setEstornarConta(null);
      setMotivoEstorno('');
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
    finally { setEstornando(false); }
  }

  async function abrirHistorico(c: ContaReceber) {
    setHistoricoConta(c);
    setHistoricoEventos(null);
    try {
      const r: any = await api.get(`/api/contas-receber/${c.id}/eventos`);
      setHistoricoEventos(Array.isArray(r) ? r : []);
    } catch (e: any) { toast.erro(e.message); setHistoricoEventos([]); }
  }

  async function excluirConta() {
    if (!confirmarExcluir) return;
    try {
      await api.delete(`/api/contas-receber/${confirmarExcluir.id}`);
      toast.sucesso('conta excluida');
      setConfirmarExcluir(null);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  async function abrirExtrato() {
    if (contasSelecionadas.length === 0) return;
    try {
      const ids = Array.from(selecionados).join(',');
      const r = await api.get<ExtratoData>('/api/contas-receber/extrato-itens', { query: { ids } });
      setItensPorCR((p) => ({ ...p, ...r }));
      setModalExtrato(true);
    } catch (e: any) { toast.erro(e.message); }
  }

  function imprimirExtrato() {
    if (contasSelecionadas.length === 0) return;
    const cliente = contasSelecionadas[0]?.cliente_nome || 'Cliente';
    const cpf = contasSelecionadas[0]?.cliente_cpf_cnpj || '';
    const contasFmt: ContaParaExtrato[] = contasSelecionadas.map((c) => ({
      id: c.id,
      orcamento_numero: c.orcamento_numero,
      data_emissao: c.data_emissao,
      valor: Number(c.valor),
    }));
    const html = gerarExtratoHTML({
      cliente, cliente_cpf_cnpj: cpf,
      contas: contasFmt,
      extratoData: itensPorCR,
      totalSelecionado,
    });
    imprimirHTML(html);
  }

  // O PDF vem PRONTO do servidor — nao passa pelo dialogo do Chrome. Motivo: escolher
  // "Salvar como PDF" ali trava em "Salvando..." pra sempre, com a previa desenhada certa; o
  // extrato da Venda CM levou as duas tentativas de conserto no navegador (largura de folha e
  // Blob URL no lugar de about:blank, DA-417) e so parou de travar quando o arquivo passou a
  // sair do servidor. O botao Imprimir ao lado segue servindo o PAPEL, que nunca quebrou.
  async function baixarExtratoPdf() {
    if (contasSelecionadas.length === 0 || baixandoExtratoPdf) return;
    setBaixandoExtratoPdf(true);
    try {
      const token = getToken();
      const r = await fetch('/api/contas-receber/extrato-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ids: contasSelecionadas.map((c) => c.id) }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ erro: String(r.status) })) as { erro?: string };
        throw new Error(j.erro || 'falha ao gerar o PDF');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Extrato ${contasSelecionadas[0]?.cliente_nome ?? 'cliente'}.pdf`.replace(/[\\/:*?"<>|]/g, '');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revogar na mesma pilha CANCELA o download antes de comecar (CLAUDE.md § download de blob).
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) { toast.erro(e.message); }
    finally { setBaixandoExtratoPdf(false); }
  }

  function abrirEnviarWhatsApp() {
    if (contasSelecionadas.length === 0) return;
    setEnviarWhatsAberto(true);
  }

  function montarMensagemExtrato(): string {
    const cliente = contasSelecionadas[0]?.cliente_nome || 'Cliente';
    const linhas: string[] = [`*Extrato de titulos em aberto*`, `Cliente: ${cliente}`, ''];
    for (const c of contasSelecionadas) {
      const ref = c.orcamento_numero ? `Orc ${c.orcamento_numero}` : (c.origem_legado ?? `CR-${c.id}`);
      const venc = formatDataSP(c.data_vencimento);
      const status = c.status === 'pago' ? ' (pago)' : c.vencido ? ` (vencido ${c.dias_atraso}d)` : '';
      linhas.push(`- ${ref} venc ${venc}: ${formatMoeda(Number(c.valor))}${status}`);
    }
    linhas.push('');
    linhas.push(`*TOTAL: ${formatMoeda(totalSelecionado)}*`);
    return linhas.join('\n');
  }

  // ⚠️ **`Parcial` e' selo proprio, nao um `Pago` com ressalva** ([DA-654]): o titulo foi encerrado
  // recebendo MENOS que o valor dele, e dizer so "Pago" afirma que entrou o valor cheio. O ambar
  // `#fde68a` + preto e' o destaque da casa (daltonismo — verde/vermelho nao servem), e quem
  // distingue de `Pendente` e' a PALAVRA, nao a cor: na aba Pagas os dois nunca aparecem juntos.
  function statusBadge(c: ContaReceber) {
    if (ehBaixaParcial(c)) {
      return (
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 4,
          background: '#fde68a', color: '#000', fontSize: 12, fontWeight: 700,
        }}>
          Parcial
        </span>
      );
    }
    if (c.status === 'pago') return <span className="badge-verde">Pago</span>;
    if (c.status === 'cancelada') return <span className="badge-vermelho">Cancelada</span>;
    if (c.vencido) return <span className="badge-vermelho">Vencida ({c.dias_atraso}d)</span>;
    return (
      <>
        <span className="badge-amarelo">Pendente</span>
        {/* Titulo que nasceu de uma baixa parcial diz de quanto ele veio e o que ja entrou —
            senao ele fica identico a um titulo qualquer, e o cliente aparece devendo R$ 73,00
            sem nenhum vestigio dos R$ 66,00 que ele ja pagou. */}
        {ehTituloDeSaldo(c) && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'nowrap' }}>
            já pago {formatMoeda(jaPagoAntesDoSaldo(c))}
          </div>
        )}
      </>
    );
  }

  // A celula VALOR. Titulo comum imprime o valor e pronto; parcial e saldo ganham a segunda linha
  // com o valor de origem recuado — o numero EM USO grande, o total recuando ([DA-202]).
  // ⚠️ `nowrap` na linha de baixo: medido na tela publicada, sem ele o texto quebra na coluna de
  // 110px e a linha engorda de 51 pra 89px.
  function celulaValor(c: ContaReceber) {
    const parcial = ehBaixaParcial(c);
    const saldo = ehTituloDeSaldo(c);
    const principal = parcial ? Number(c.valor_pago ?? 0) : Number(c.valor);
    const origem = parcial ? Number(c.valor) : Number(c.valor_original ?? 0);
    return (
      <>
        {/* Sem "R$": ele mora no titulo da coluna, "Valor (R$)" (pedido dele, 16/10/2026). */}
        <div style={{ fontWeight: 600 }}>{formatMoedaSemSimbolo(principal)}</div>
        {(parcial || saldo) && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, whiteSpace: 'nowrap' }}>
            de {formatMoedaSemSimbolo(origem)}
          </div>
        )}
      </>
    );
  }

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Wallet size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Contas a Receber
        </h2>
      </div>

      {/* Abas de status */}
      <div className="abas">
        {[
          { st: 'pendente', label: 'Pendentes' },
          { st: 'vencido', label: 'Vencidas' },
          { st: 'pago', label: 'Pagas' },
          { st: 'cancelada', label: 'Canceladas' },
          { st: 'todos', label: 'Todas' },
          { st: 'extrato', label: 'Extrato do Cliente' },
          { st: 'boletos', label: 'Boletos' },
        ].map((aba) => (
          <button
            key={aba.st}
            type="button"
            className={`aba-btn ${filtro === aba.st ? 'aba-btn-ativo' : ''}`}
            onClick={() => selecionarAba(aba.st)}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Aba Extrato do Cliente — seletor de cliente + botao Carregar (nada automatico) */}
      {filtro === 'extrato' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8,
            padding: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Cliente</div>
              {extratoCliente ? (
                <div style={{ fontSize: 15, fontWeight: 600 }}>
                  {extratoCliente.nome}
                  {extratoCliente.codigo_legado && (
                    <span className="text-gray-500 font-bold no-underline" style={{ fontSize: 13, marginLeft: 8 }}>
                      {extratoCliente.codigo_legado}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Nenhum cliente selecionado</div>
              )}
            </div>
            <button type="button" className="btn-secundario" onClick={() => setBuscaClienteAberta(true)}>
              <Search size={14} style={{ display: 'inline', marginRight: 4 }} />
              {extratoCliente ? 'Trocar cliente' : 'Escolher cliente'}
            </button>
            <button
              type="button"
              onClick={() => setExtratoCarregado(true)}
              disabled={!extratoCliente}
              style={{
                height: 38, padding: '0 18px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
                color: '#fff', background: extratoCliente ? '#16a34a' : '#9ca3af',
                cursor: extratoCliente ? 'pointer' : 'not-allowed',
              }}
            >
              Carregar
            </button>
          </div>

          {extratoCarregado && extratoCliente ? (
            <ExtratoCliente key={extratoCliente.id} clienteId={extratoCliente.id} embutido />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              {extratoCliente
                ? 'Clique em Carregar para ver o extrato completo do cliente.'
                : 'Escolha um cliente e clique em Carregar.'}
            </div>
          )}
        </div>
      )}

      {/* Busca */}
      {filtro !== 'extrato' && (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="search"
            placeholder="Buscar cliente, codigo ou orcamento..."
            value={busca}
            onChange={(e) => setBusca(upperMantendoCursor(e))}
            style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
            autoFocus
          />
        </div>
      </div>
      )}

      {/* Tabela */}
      {filtro !== 'extrato' && (loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                {filtro !== 'cancelada' && (
                  <input
                    type="checkbox"
                    checked={selecionaveisVisiveis.length > 0 && selecionados.size === selecionaveisVisiveis.length}
                    onChange={toggleTodos}
                  />
                )}
              </th>
              <th style={{ width: 32 }}></th>
              <th style={{ width: 40, textAlign: 'right' }}>#</th>
              {/* 🧾 Nota entre `#` e `Cod` — a posicao que ele MARCOU no print ([DA-632]).
                  ⚠️ 70px e' o que a palavra "Consumidor" pede em 10,5px; abaixo disso ela
                  quebra e a linha inteira engorda. */}
              <th style={{ width: 70 }}>Nota</th>
              <th style={{ width: 60 }}>Cod</th>
              <th>Cliente</th>
              {!ehAbaPendentes && <th style={{ width: 120 }}>Orcamento</th>}
              <th style={{ width: 110 }}>Emissao</th>
              <th style={{ width: 110 }}>Vencimento</th>
              {/* Valor e Valor Pago no justo, SEM quebrar, com o "R$" no TITULO e nao em cada linha
                  (pedidos dele, 16/10/2026). Medido: "1.499,00" = 67px em mono 14px (o maior valor da
                  base, 3.081,33, tem o mesmo tamanho). Titulos curtos por pedido dele ("VLR R$" /
                  "VLR PG R$"), pra que o valor e nao o titulo decida a largura: 67 + 6px de cada lado. */}
              <th style={{ textAlign: 'right', width: 80, paddingLeft: 6, paddingRight: 6, whiteSpace: 'nowrap' }}>Vlr R$</th>
              {/* Valor Pago + Forma entre Valor e Vendedor — onde ele MARCOU no print (16/10/2026).
                  Valor segue sendo a base da comissao; Valor Pago e' o que o cliente pagou (0321). */}
              {!semColunasDePagamento && <th style={{ textAlign: 'right', width: 80, paddingLeft: 6, paddingRight: 6, whiteSpace: 'nowrap' }}>Vlr Pg R$</th>}
              {/* Data do pagamento entre Valor Pago e Forma — onde ele marcou (16/10/2026). */}
              {!semColunasDePagamento && <th style={{ width: 100 }}>Pago em</th>}
              {/* "Crediario" (51px em 12px) e' o maior rotulo depois de "Cartao Credito" virar
                  "Credito" (pedido dele, 16/10/2026): 51 + 6px de cada lado. */}
              {!semColunasDePagamento && <th style={{ width: 64, paddingLeft: 6, paddingRight: 6, whiteSpace: 'nowrap' }}>Forma</th>}
              <th style={{ width: 130 }}>Vendedor</th>
              {/* Na aba Pagas todo titulo e' pago: a coluna sai (pedido dele, 16/10/2026). O "Parcial"
                  que ela mostrava (14 de 441) segue visivel no VLR R$ — valor pago + "de <original>". */}
              {!ehAbaPagas && <th style={{ width: 130 }}>Status</th>}
              <th style={{ width: 140 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {contasFiltradas.length === 0 ? (
              <tr><td colSpan={totalColunas} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma conta encontrada</td></tr>
            ) : contasFiltradas.map((c, idx) => {
              const rp = grid.rowProps(c);
              return (
              <Fragment key={c.id}>
                <tr
                  {...rp}
                  style={{
                    // Vencido pinta o tint vermelho; a selecao (amarelo, em rp.style) vence quando ativa.
                    ...(c.vencido && c.status === 'pendente' ? { background: 'rgba(220,38,38,0.06)' } : {}),
                    ...rp.style,
                  }}
                >
                  <td>
                    {(c.status === 'pendente' || (ehAbaPagas && c.status === 'pago')) && (
                      <input
                        type="checkbox"
                        checked={selecionados.has(c.id)}
                        onChange={() => toggleSelecionado(c.id)}
                      />
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => toggleExpand(c.id)}
                      className="btn-icon"
                      title="Ver itens do orcamento"
                    >
                      {expandedIds.has(c.id) ? <Minus size={14} /> : <Plus size={14} />}
                    </button>
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                  <CelulaNota tipo={c.nota_tipo} numero={c.nota_numero} />
                  <td className="text-gray-500 font-bold no-underline">{c.cliente_codigo_legado || '-'}</td>
                  <td>{c.cliente_nome}</td>
                  {/* ⚠️ `nowrap`: com a coluna Nota entrando ([DA-632]), a coluna encolheu pra
                      104px e `ORC-004565` passou a quebrar em duas linhas — engordando TODA a
                      linha de 41 pra 61px. Numero de pedido e' identificador que ele usa pra
                      achar o item; quebrado no meio, para de servir pra isso. */}
                  {!ehAbaPendentes && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.orcamento_numero || (c.origem_legado ? <span style={{ color: 'var(--text-muted)' }}>{c.origem_legado}</span> : '-')}
                      {/* Sem isto, o saldo e o titulo que o gerou aparecem como DUAS linhas do mesmo
                          orcamento, com valores diferentes e nada ligando uma a outra. */}
                      {ehTituloDeSaldo(c) && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> (saldo)</span>}
                    </td>
                  )}
                  <td>{formatDataSP(c.data_emissao)}</td>
                  <td>{formatDataSP(c.data_vencimento)}</td>
                  <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600, paddingLeft: 6, paddingRight: 6, whiteSpace: 'nowrap' }}>{celulaValor(c)}</td>
                  {!semColunasDePagamento && (
                    <>
                      <td className="cel-num" style={{ textAlign: 'right', fontWeight: 600, paddingLeft: 6, paddingRight: 6, whiteSpace: 'nowrap' }}>
                        {c.status === 'pago' ? formatMoedaSemSimbolo(valorPagoCliente(c)) : '—'}
                      </td>
                      {/* `data_pagamento` e' date pura: formatDataSP, nunca new Date() (regride um dia). */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {c.status === 'pago' && c.data_pagamento ? formatDataSP(c.data_pagamento) : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', paddingLeft: 6, paddingRight: 6 }}>
                        {/* So' o ROTULO desta coluna encurta ("Cartao Credito" -> "Credito"); o cadastro
                            da forma segue com o nome inteiro, que o recibo, a nota e o caixa usam. */}
                        <span style={{ fontSize: 12 }}>{c.status === 'pago' ? (c.forma_pagamento_nome?.replace(/^Cart[aã]o\s+/i, '') ?? '—') : '—'}</span>
                      </td>
                    </>
                  )}
                  <td>
                    <span style={{ fontSize: 12 }}>{c.vendedor_nome ?? '—'}</span>
                  </td>
                  {!ehAbaPagas && <td>{statusBadge(c)}</td>}
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {podeReceber && c.status === 'pendente' && (
                        <button
                          type="button"
                          onClick={() => abrirReceberUnitario(c)}
                          className="bg-blue-600 text-white hover:bg-blue-700"
                          title="Receber este titulo"
                          style={{ padding: '4px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          Receber
                        </button>
                      )}
                      {c.status === 'pendente' && cobrancaDisp.cartao && (
                        <button
                          type="button"
                          onClick={() => { setSimProvedor('Mercado Pago'); setSimValorReceber(Number(c.valor) || 0); setSimularTitulo(c); }}
                          className="btn-icon"
                          title="Simular parcelamento e enviar por WhatsApp"
                          style={{ color: '#166534' }}
                        >
                          <MessageCircle size={14} />
                        </button>
                      )}
                      {podeReceber && (
                        <button
                          type="button"
                          onClick={() => toggleExibirCaixa(c)}
                          className="btn-icon"
                          title={c.exibir_no_caixa ? 'Ocultar do caixa' : 'Exibir no caixa'}
                        >
                          {c.exibir_no_caixa ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                      )}
                      {/* Boleto no AppCel ([DA-629]). ⚠️ So em titulo PENDENTE: o app
                          lista boleto pelo mesmo recorte de "em aberto", entao anexar
                          a uma conta paga gravaria um papel que ninguem veria. O icone
                          MUDA de estado (cheio quando ja tem) — o principio 1 vale aqui
                          tanto quanto na tela do cliente. */}
                      {podeCancelar && c.status === 'pendente' && (
                        <button
                          type="button"
                          onClick={() => setBoletoContas([c])}
                          className="btn-icon"
                          title={c.boleto_id ? 'Boleto ja no aplicativo do cliente — ver, trocar ou tirar' : 'Anexar boleto pro cliente baixar no aplicativo'}
                          style={c.boleto_id ? { color: 'var(--primary)' } : undefined}
                        >
                          <Barcode size={14} />
                        </button>
                      )}
                      {podeCancelar && c.status === 'pendente' && (
                        <button
                          type="button"
                          onClick={() => setCancelarConta(c)}
                          className="btn-icon"
                          title="Cancelar conta"
                        >
                          <Ban size={14} />
                        </button>
                      )}
                      {isAdmin && c.status !== 'pago' && (
                        <button
                          type="button"
                          onClick={() => setConfirmarExcluir(c)}
                          className="btn-icon"
                          title="Excluir (admin)"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {c.status === 'pago' && (
                        <button
                          type="button"
                          onClick={() => reciboDaBaixa(c)}
                          className="btn-icon"
                          title="Imprimir o recibo desta baixa (todos os titulos pagos junto com este)"
                        >
                          <Printer size={14} />
                        </button>
                      )}
                      {c.status === 'pago' && (
                        <button
                          type="button"
                          onClick={() => void notasDaBaixa(c)}
                          className="btn-icon"
                          disabled={baixandoNotas === c.id}
                          title="Baixar as notas fiscais desta baixa (PDF unico, pronto pra mandar no WhatsApp)"
                        >
                          {baixandoNotas === c.id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                        </button>
                      )}
                      {c.status === 'pago' && (
                        <button
                          type="button"
                          onClick={() => setEnviarNotasConta(c)}
                          className="btn-icon"
                          title="Enviar as notas fiscais desta baixa pelo WhatsApp do cliente"
                        >
                          <MessageCircle size={14} />
                        </button>
                      )}
                      {isAdmin && c.status === 'pago' && (
                        <button
                          type="button"
                          onClick={() => { setEstornarConta(c); setMotivoEstorno(''); }}
                          className="btn-icon"
                          title="Estornar a baixa — devolve o titulo pra em aberto (admin)"
                        >
                          <Undo2 size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void abrirHistorico(c)}
                        className="btn-icon"
                        title="Historico do titulo — quem baixou, quando e de onde"
                      >
                        <History size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedIds.has(c.id) && (
                  <tr>
                    <td colSpan={totalColunas} style={{ background: 'var(--card-bg)', padding: 12 }}>
                      {itensPorCR[c.id] === undefined ? (
                        <span style={{ color: 'var(--text-muted)' }}>Carregando...</span>
                      ) : (itensPorCR[c.id]?.itens.length || 0) === 0 ? (
                        <span style={{ color: 'var(--text-muted)' }}>Sem itens disponiveis</span>
                      ) : (
                        <>
                          {itensPorCR[c.id]?.comprador && (
                            <div style={{ marginBottom: 8, fontSize: 13 }}><strong>Comprador:</strong> {itensPorCR[c.id]!.comprador}</div>
                          )}
                          <table style={{ width: '100%', fontSize: 13 }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                <th style={{ width: 56 }}></th>
                                <th style={{ width: 60, textAlign: 'left' }}>Cod</th>
                                <th style={{ textAlign: 'left' }}>Produto</th>
                                <th style={{ width: 80, textAlign: 'right' }}>Qtd</th>
                                <th style={{ width: 100, textAlign: 'right' }}>Unit.</th>
                                <th style={{ width: 100, textAlign: 'right' }}>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {itensPorCR[c.id]!.itens.map((it, idx) => (
                                <tr key={idx}>
                                  {/* padding proprio: o padrao da celula (10px 12px) espremia a foto pra 16px de largura; 48px cabe na altura que a linha ja tinha */}
                                  <td style={{ textAlign: 'center', padding: '2px 4px' }}>
                                    {it.foto_url ? (
                                      <img src={it.foto_url} alt="" style={{ width: 48, height: 48, maxWidth: 'none', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                                    ) : null}
                                  </td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: 11 }}>{it.codigo}</td>
                                  <td>{it.produto}</td>
                                  <td className="cel-num" style={{ textAlign: 'right' }}>{it.qtd % 1 === 0 ? it.qtd : it.qtd.toFixed(3)} {it.unidade || ''}</td>
                                  <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(it.preco)}</td>
                                  <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(it.subtotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      ))}

      {/* Barra inferior — selecao */}
      {filtro !== 'extrato' && contasSelecionadas.length > 0 && (
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 12,
          background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8,
          padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 -2px 6px rgba(0,0,0,0.1)',
        }}>
          <div>
            <strong className="cel-num">{contasSelecionadas.length}</strong> titulo(s) selecionado(s) — total <strong className="cel-num-forte">{formatMoeda(totalSelecionado)}</strong>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secundario" onClick={abrirExtrato}>Extrato</button>
            {/* 🔑 **Anexar UM boleto pros titulos marcados** ([DA-630]). E' a porta que
                faltava: ele junta varias notas num boleto so, e sem ela ia digitar
                "notas 243, 244, 245" a mao num campo livre. So aparece com titulos
                PENDENTES e de um cliente so — as duas condicoes que o servidor recusa,
                pra tela nunca oferecer o que a rota vai negar. */}
            {!ehAbaPagas && podeCancelar && podeAnexarBoleto && (
              <button type="button" className="btn-secundario" onClick={() => setBoletoContas(contasSelecionadas)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <Barcode size={14} />
                {contasSelecionadas.length > 1 ? `Boleto dos ${contasSelecionadas.length}` : 'Anexar boleto'}
              </button>
            )}
            {ehAbaPagas ? (
              <button type="button" className="btn-primario" onClick={reimprimirRecibo}>
                <Printer size={14} style={{ display: 'inline', marginRight: 4 }} />
                Imprimir recibo
              </button>
            ) : podeReceber && (
              <button type="button" className="btn-primario" onClick={abrirModalLote}>
                <Check size={14} style={{ display: 'inline', marginRight: 4 }} />
                Receber {contasSelecionadas.length} titulo(s)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal Baixa Individual — segue resize global de DA-010 */}
      <Modal
        aberto={!!baixarConta}
        onFechar={() => { if (!baixaEnviando) { setCobrancaAtiva(null); setBaixarConta(null); } }}
        titulo="Receber Titulo"
        largura={720}
      >
        {baixarConta && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -4 }}>{baixarConta.cliente_nome}</div>

            <div style={{
              background: 'var(--card-bg, rgba(124,58,237,0.08))',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '10px 12px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Valor da conta</span>
                <span className="cel-num-forte" style={{ fontSize: 20, fontWeight: 700 }}>{formatMoeda(baixarConta.valor)}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)' }}>
                <span>Emissao {formatDataSP(baixarConta.data_emissao)}</span>
                {(baixarConta.orcamento_numero || baixarConta.origem_legado) && (
                  <span>Origem {baixarConta.orcamento_numero || baixarConta.origem_legado}</span>
                )}
              </div>
            </div>

            {cobrancaAtiva ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', textTransform: 'none' }}>
                  Cobrando <strong>{formatMoeda(cobrancaValor)}</strong> — o título baixa e concilia sozinho quando o cliente pagar.
                </div>
                {cobrancaAtiva.tipo === 'pix' ? (
                  <CobrancaPix
                    alvo={{ tipo: 'titulo', clienteId: baixarConta.cliente_id, contaReceberId: baixarConta.id }}
                    valor={cobrancaValor}
                    layout="cheio"
                    mostrarComprovante
                    onValorAjustado={(v) => setCobrancaValor(v)}
                    onPago={(recId) => void baixarTituloComRecebimento('pix', recId ?? null)}
                    onCancelado={() => setCobrancaAtiva(null)}
                  />
                ) : (
                  <CobrancaCartao
                    alvo={{ tipo: 'titulo', clienteId: baixarConta.cliente_id, contaReceberId: baixarConta.id }}
                    valor={cobrancaValor}
                    cardType={cobrancaAtiva.cardType ?? 'credit'}
                    layout="cheio"
                    onAprovado={(recId) => void baixarTituloComRecebimento('avulso', recId ?? null)}
                    onCancelado={() => setCobrancaAtiva(null)}
                    imprimirVia={imprimirViaCartao}
                  />
                )}
                <button
                  type="button" className="btn-secundario" onClick={() => setCobrancaAtiva(null)}
                  disabled={baixaEnviando} style={{ alignSelf: 'center' }}
                >
                  Voltar
                </button>
              </div>
            ) : (
            <>
            {/* Duas colunas: Vencimento|Valor Pago e Forma|Caixa lado a lado, pro modal caber sem rolar. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Vencimento</label>
              <input
                type="date"
                value={vencEdit}
                onChange={(e) => setVencEdit(e.target.value)}
                disabled={!podeCancelar || baixaEnviando || salvandoVenc}
                title={podeCancelar ? 'Alterar data de vencimento — grava no botão Salvar' : undefined}
                style={{ width: '100%', height: 42, fontSize: 15, textAlign: 'center' }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <label className="form-label">Valor Pago *</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Exibir este titulo na tela do Caixa quando o cliente fizer novas compras">
                  <input
                    type="checkbox"
                    checked={baixaForm.exibir_no_caixa}
                    onChange={(e) => setBaixaForm({ ...baixaForm, exibir_no_caixa: e.target.checked })}
                  />
                  Exibir no Caixa
                </label>
              </div>
              <MoneyInput
                valor={baixaForm.valor_pago}
                onChangeValor={(v) => setBaixaForm({ ...baixaForm, valor_pago: v })}
                style={{ width: '100%', height: 42, fontSize: 15, textAlign: 'center' }}
              />
            </div>
            </div>

            {baixaForm.valor_pago > 0 && baixaForm.valor_pago < Number(baixarConta.valor) && (
              <div style={{ fontSize: 13, color: 'var(--primary)', marginTop: -4, fontWeight: 500 }}>
                Pagamento parcial — saldo de <span className="cel-num">{formatMoeda(Number(baixarConta.valor) - baixaForm.valor_pago)}</span> sera gerado automaticamente
              </div>
            )}

            {baixaPixSel ? (
              <div style={{ display: 'flex' }}>
                <FormaCaixaTravada rec={baixaPixSel} />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Forma de Pagamento *</label>
                  <select
                    value={baixaForm.forma_pagamento_id}
                    onChange={(e) => setBaixaForm({ ...baixaForm, forma_pagamento_id: e.target.value, caixa_id: '' })}
                    style={{ width: '100%', height: 42, fontSize: 15, textAlign: 'center', textAlignLast: 'center' }}
                  >
                    <option value="">Selecione</option>
                    {formasPagamento.map((fp) => (
                      <option key={fp.id} value={fp.id}>{fp.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Caixa Destino *</label>
                  <select
                    value={baixaForm.caixa_id}
                    onChange={(e) => setBaixaForm({ ...baixaForm, caixa_id: e.target.value })}
                    disabled={!baixaForm.forma_pagamento_id || baixaCaixasLoading || baixaCaixas.length === 0}
                    style={{ width: '100%', height: 42, fontSize: 15, textAlign: 'center', textAlignLast: 'center' }}
                  >
                    <option value="">
                      {!baixaForm.forma_pagamento_id ? 'Selecione a forma' : baixaCaixasLoading ? 'Carregando...' : baixaCaixas.length === 0 ? 'Nenhum caixa aceita' : 'Selecione...'}
                    </option>
                    {baixaCaixas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Vincular a um recebimento livre (Pix do MP + avulsos do cliente), FILTRADO pela conta
                escolhida. Aparece assim que a lista carrega — inclusive vazia, dizendo por quê: some
                calado foi o que fez concluir que nao havia como conciliar. Ver [DA-593]. */}
            {baixaLivresDaConta && (
              <SeletorRecebimentoLivre
                recebimentos={baixaLivresDaConta}
                selecionado={baixaPixSel}
                onSelecionar={escolherRecebimentoSingle}
                totalTitulos={baixaForm.valor_pago}
                caixaNome={baixaCaixas.find((c) => String(c.id) === baixaForm.caixa_id)?.nome ?? null}
                escapeVisivel={ofereceCobrarSingle}
                acrescimo={baixaAcrescimo}
                onAcrescimo={setBaixaAcrescimo}
              />
            )}

            <div>
              <label className="form-label">Observacoes (opcional)</label>
              <textarea
                value={baixaForm.observacoes}
                onChange={(e) => setBaixaForm({ ...baixaForm, observacoes: e.target.value })}
                rows={2}
                style={{ width: '100%', fontSize: 15, padding: '8px 12px', resize: 'vertical' }}
              />
            </div>

            {/* O valor já é ajustado ao escolher o recebimento. Esta linha só aparece se o operador digitar
                um valor acima do que o recebimento cobre — aí o Confirmar trava (não quita sem lastro). */}
            {baixaPixSel && Math.min(baixaForm.valor_pago, Number(baixarConta.valor)) > Number(baixaPixSel.disponivel) + 0.005 && (
              <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                O valor passou do recebimento ({formatMoeda(Number(baixaPixSel.disponivel))}). Reduza — o recebimento só cobre até aí.
              </div>
            )}

            {/* "Já recebido" — escape pra Pix/Cartão que caiu FORA do sistema (ex: Pix direto na Cora,
                sem feed pra casar). Marcado, a cobrança ao vivo desliga e o botão vira "Confirmar Baixa"
                na conta escolhida. Só aparece quando a forma ofereceria cobrar agora. */}
            {(() => {
              const formaSelTipo = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.tipo ?? null;
              const ehPixSel = formaSelTipo === 'pix';
              // A regra de "oferece cobrar ao vivo" mora em `ofereceCobrarSingle` (uma copia so).
              if (!ofereceCobrarSingle) return null;
              return (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--input-border)', background: 'var(--panel-bg)', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={baixaForm.ja_recebido}
                    onChange={(e) => setBaixaForm({ ...baixaForm, ja_recebido: e.target.checked })}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>
                    <strong>Já recebido — só registrar</strong> (não cobrar agora)
                    <br />
                    <span style={{ color: 'var(--text-muted)' }}>
                      O {ehPixSel ? 'Pix' : 'pagamento'} já caiu na conta (ex: Pix direto na Cora). Marque pra dar baixa na conta escolhida, sem gerar uma cobrança nova.
                    </span>
                  </span>
                </label>
              );
            })()}

            {(() => {
              const formaSelTipo = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.tipo ?? null;
              const ehCartaoSel = formaSelTipo === 'cartao_credito' || formaSelTipo === 'cartao_debito';
              if (!ehCartaoSel || !ofereceCobrarSingle || baixaForm.ja_recebido) return null;
              return <ImprimirViaCartao marcado={imprimirViaCartao} onChange={setImprimirViaCartao} />;
            })()}

            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setBaixarConta(null)}
                disabled={baixaEnviando}
                style={{
                  flex: 1, height: 46, fontSize: 15, fontWeight: 600,
                  background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              {/* Salvar: grava so' o vencimento (a unica coisa editavel sem dar baixa) e fecha. Some pra
                  quem nao pode alterar a data — ali nao haveria o que salvar. */}
              {podeCancelar && (
                <button
                  type="button"
                  onClick={async () => { if (await salvarVencimento()) setBaixarConta(null); }}
                  disabled={baixaEnviando || salvandoVenc}
                  title="Salva a data de vencimento sem dar baixa no título"
                  style={{
                    flex: 1, height: 46, fontSize: 15, fontWeight: 600,
                    background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                    cursor: (baixaEnviando || salvandoVenc) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {salvandoVenc ? 'Salvando...' : 'Salvar'}
                </button>
              )}
              {(() => {
                // Forma Pix/Cartão com provedor disponível e sem recebimento já casado → COBRA de verdade
                // (peça 013/016) em vez de baixar na fé. Demais formas (dinheiro) seguem na baixa direta.
                const formaSelTipo = formasPagamento.find((f) => String(f.id) === baixaForm.forma_pagamento_id)?.tipo ?? null;
                const ehPixSel = formaSelTipo === 'pix';
                // "Já recebido" desliga a cobrança ao vivo e cai na baixa direta (Pix que caiu fora do
                // sistema — ex: direto na Cora, que não tem feed pra casar/confirmar sozinho).
                const podeCobrarAgora = ofereceCobrarSingle && !baixaForm.ja_recebido;
                if (podeCobrarAgora) {
                  const vpOk = baixaForm.valor_pago > 0 && baixaForm.valor_pago <= Number(baixarConta.valor) + 0.005;
                  return (
                    <button
                      type="button" onClick={iniciarCobranca} disabled={baixaEnviando || !vpOk}
                      title={!vpOk ? 'Informe um valor válido (até o valor da conta)' : undefined}
                      style={{
                        flex: 1, height: 46, fontSize: 15, fontWeight: 600,
                        background: !vpOk ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                        cursor: (baixaEnviando || !vpOk) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {ehPixSel ? 'Cobrar via Pix' : 'Cobrar no cartão'}
                    </button>
                  );
                }
                const recExcede = !!baixaPixSel && Math.min(baixaForm.valor_pago, Number(baixarConta.valor)) > Number(baixaPixSel.disponivel) + 0.005;
                const faltaFormaCaixa = !baixaPixSel && (!baixaForm.forma_pagamento_id || !baixaForm.caixa_id);
                const bloqueado = faltaFormaCaixa || recExcede;
                return (
              <button
                type="button"
                onClick={confirmarBaixaSingle}
                disabled={baixaEnviando || bloqueado}
                title={faltaFormaCaixa ? 'Escolha a forma e o caixa antes de confirmar' : recExcede ? 'O valor passa do recebimento — ajuste antes de confirmar' : undefined}
                style={{
                  flex: 1, height: 46, fontSize: 15, fontWeight: 600,
                  background: bloqueado ? '#9ca3af' : '#16a34a',
                  color: '#fff', border: 'none', borderRadius: 8,
                  cursor: (baixaEnviando || bloqueado) ? 'not-allowed' : 'pointer',
                }}
              >
                {baixaEnviando ? 'Recebendo...' : 'Confirmar Baixa'}
              </button>
                );
              })()}
            </div>
            </>
            )}
          </div>
        )}
      </Modal>

      {/* Modal Simular Parcelamento (peça 014) — porta PRÓPRIA, separada da baixa. So mostra o valor
          do título + o botão que gera a imagem e abre o envio por WhatsApp. NUNCA baixa/cobra nada. */}
      <Modal
        aberto={!!simularTitulo}
        onFechar={() => setSimularTitulo(null)}
        titulo="Simular Parcelamento"
        largura={440}
      >
        {simularTitulo && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -4 }}>{simularTitulo.cliente_nome}</div>
            <div style={{
              background: 'var(--card-bg, rgba(124,58,237,0.08))', border: '1px solid var(--border)',
              borderRadius: 8, padding: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Valor do título</span>
                <span className="cel-num-forte" style={{ fontSize: 20, fontWeight: 700 }}>{formatMoeda(simularTitulo.valor)}</span>
              </div>
              {(simularTitulo.orcamento_numero || simularTitulo.origem_legado) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                  <span>Origem</span>
                  <span>{simularTitulo.orcamento_numero || simularTitulo.origem_legado}</span>
                </div>
              )}
            </div>

            {/* Valor a receber (líquido) — default do título, ajustável. É a base da simulação. */}
            <div>
              <label className="form-label">Quanto você quer receber? (líquido)</label>
              <MoneyInput
                valor={simValorReceber}
                onChangeValor={(v) => setSimValorReceber(v)}
                style={{ width: '100%', height: 46, fontSize: 15, textAlign: 'center' }}
              />
              {simParcelamento.data && !simParcelamento.loading && !simParcelamento.erro && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 4, textTransform: 'none' }}>
                  Cai pra você (líquido): <strong style={{ color: 'var(--text)' }}>{formatMoeda(simParcelamento.data.base_pix / 100)}</strong> — o cliente paga a taxa por cima.
                </div>
              )}
            </div>

            {/* Maquininha — as parcelas/juros mudam por adquirente, então o operador ESCOLHE. */}
            <div>
              <label className="form-label">Maquininha</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['Mercado Pago', 'PagBank'] as const).map((prov) => (
                  <button
                    key={prov}
                    type="button"
                    onClick={() => setSimProvedor(prov)}
                    style={{
                      flex: 1, height: 40, borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer',
                      border: simProvedor === prov ? '2px solid #166534' : '1px solid var(--input-border)',
                      background: simProvedor === prov ? '#fde68a' : 'var(--panel-bg)',
                      color: simProvedor === prov ? '#000' : 'var(--text-muted)',
                    }}
                  >
                    {prov}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabela de parcelas do provedor escolhido — SÓ exibe (não recalcula nada). */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, minHeight: 64 }}>
              {simParcelamento.loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 8 }}>Calculando parcelas…</div>
              ) : simParcelamento.erro ? (
                <div style={{ textAlign: 'center', color: '#b91c1c', fontSize: 13, padding: 8 }}>
                  {simProvedor} não está configurado pra parcelamento.
                </div>
              ) : simParcelamento.data && simParcelamento.data.parcelas.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {simParcelamento.data.parcelas.map((x) => (
                    <div key={x.n} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, padding: '3px 4px', borderBottom: '1px solid var(--border)' }}>
                      <span><strong>{x.n}x</strong> de {formatMoeda(x.valor_parcela / 100)}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {x.sem_juros ? 'sem juros' : x.juros_mes ? `${x.juros_mes.toFixed(1).replace('.', ',')}% a.m.` : ''}
                        {' · total '}{formatMoeda(x.total / 100)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 8 }}>Sem parcelamento disponível pra este valor.</div>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', textTransform: 'none' }}>
              Só uma simulação pra mandar ao cliente — não cobra nem baixa o título.
            </div>

            {simParcelamento.data && simParcelamento.data.parcelas.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <SimulacaoParcelamentoWhatsApp
                  parcelas={simParcelamento.data.parcelas}
                  provedor={simProvedor}
                  orcamentoId={simularTitulo.orcamento_id}
                  numero={simularTitulo.orcamento_numero || simularTitulo.origem_legado}
                  clienteId={simularTitulo.cliente_id}
                  clienteNome={simularTitulo.cliente_nome}
                  operador={usuario?.nome || undefined}
                  rotuloDocumento="Título"
                  onErro={(m) => toast.erro(m)}
                />
              </div>
            )}

            <button type="button" className="btn-secundario" onClick={() => setSimularTitulo(null)} style={{ alignSelf: 'center' }}>
              Fechar
            </button>
          </div>
        )}
      </Modal>

      {/* Modal Baixa em Lote — topo (forma/caixa) e rodape (total+confirmar) FIXOS; so a lista
          de titulos rola internamente. Mata o clique cego: a conta escolhida fica sempre a vista. */}
      <Modal aberto={modalLote} onFechar={() => { if (!loteEnviando) { setCobrancaLote(null); setModalLote(false); } }} titulo="Receber Titulos" largura={760}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          {cobrancaLote ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0', alignItems: 'center' }}>
              <div style={{ fontSize: 14, textAlign: 'center', textTransform: 'none' }}>
                Cobrando <strong>{formatMoeda(cobrancaLoteValor)}</strong> de <strong>{contasSelecionadas[0]?.cliente_nome}</strong> — {contasSelecionadas.length} título(s) baixam e conciliam sozinhos quando o cliente pagar.
              </div>
              {cobrancaLote.tipo === 'pix' ? (
                <CobrancaPix
                  alvo={{ tipo: 'titulo', clienteId: clienteComumLote! }}
                  valor={cobrancaLoteValor}
                  layout="cheio"
                  mostrarComprovante
                  onValorAjustado={(v) => setCobrancaLoteValor(v)}
                  onPago={(recId) => void baixarLoteComRecebimento('pix', recId ?? null)}
                  onCancelado={() => setCobrancaLote(null)}
                />
              ) : (
                <CobrancaCartao
                  alvo={{ tipo: 'titulo', clienteId: clienteComumLote! }}
                  valor={cobrancaLoteValor}
                  cardType={cobrancaLote.cardType ?? 'credit'}
                  layout="cheio"
                  onAprovado={(recId) => void baixarLoteComRecebimento('avulso', recId ?? null)}
                  onCancelado={() => setCobrancaLote(null)}
                  imprimirVia={imprimirViaCartao}
                />
              )}
              <button type="button" className="btn-secundario" onClick={() => setCobrancaLote(null)} disabled={loteEnviando}>
                Voltar
              </button>
            </div>
          ) : (
          <>
          {/* TOPO FIXO — escolha da conta */}
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {lotePixSel ? (
                <FormaCaixaTravada rec={lotePixSel} />
              ) : (
                <>
                  <div style={{ flex: '1 1 200px' }}>
                    <label className="form-label">Forma de pagamento *</label>
                    <select
                      value={loteForm.forma_pagamento_id}
                      onChange={(e) => setLoteForm({ ...loteForm, forma_pagamento_id: e.target.value, caixa_id: '' })}
                      style={{ width: '100%' }}
                    >
                      <option value="">Selecione</option>
                      {formasPagamento.map((fp) => (
                        <option key={fp.id} value={fp.id}>{fp.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1 1 200px' }}>
                    <label className="form-label">Caixa de origem *</label>
                    <select
                      value={loteForm.caixa_id}
                      onChange={(e) => setLoteForm({ ...loteForm, caixa_id: e.target.value })}
                      disabled={!loteForm.forma_pagamento_id || carregandoCaixas || loteCaixas.length === 0}
                      style={{ width: '100%' }}
                    >
                      <option value="">
                        {!loteForm.forma_pagamento_id ? 'Selecione a forma' : carregandoCaixas ? 'Carregando...' : loteCaixas.length === 0 ? 'Nenhum caixa aceita' : 'Selecione...'}
                      </option>
                      {loteCaixas.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Sera registrado como suprimento (entrada) neste caixa.
                    </div>
                  </div>
                </>
              )}
              <div style={{ flex: '1 1 200px' }}>
                <label className="form-label">Observacoes</label>
                <input
                  type="text"
                  value={loteForm.observacoes}
                  onChange={(e) => setLoteForm({ ...loteForm, observacoes: e.target.value })}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Vincular a um recebimento livre, FILTRADO pela conta escolhida (igual ao individual). */}
            {loteLivresDaConta && (
              <SeletorRecebimentoLivre
                recebimentos={loteLivresDaConta}
                selecionado={lotePixSel}
                onSelecionar={escolherRecebimentoLote}
                totalTitulos={totalLote}
                caixaNome={loteCaixas.find((c) => String(c.id) === loteForm.caixa_id)?.nome ?? null}
                escapeVisivel={ofereceCobrarLote}
                acrescimo={loteAcrescimo}
                onAcrescimo={setLoteAcrescimo}
              />
            )}
          </div>

          {/* MEIO — lista de titulos (PEÇA 017, a mesma da Conciliação Bancária: as duas telas baixam
              título casando com um recebimento, só entram por lados opostos). A peça rola por dentro,
              então o painel de conta escolhida nunca sai da vista (bug do clique cego). */}
          <div style={{ margin: '12px 0' }}>
            <TitulosParaReceber
              titulos={contasSelecionadas}
              valores={loteValores}
              onValores={setLoteValores}
              teto={lotePixSel ? Number(lotePixSel.disponivel) : undefined}
            />
          </div>

          {/* RODAPE FIXO — confirmar */}
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* "Já recebido" — escape pra Pix/cartão que caiu FORA do sistema (ex: Pix direto na Cora).
                Marcado, desliga a cobrança ao vivo do lote e volta pro Confirmar Recebimento direto.
                Mesmo padrão do modal individual (DA-262). */}
            {(() => {
              const formaSelTipo = formasPagamento.find((f) => String(f.id) === loteForm.forma_pagamento_id)?.tipo ?? null;
              const ehPixSel = formaSelTipo === 'pix';
              // A regra de "oferece cobrar ao vivo" mora em `ofereceCobrarLote` (uma copia so).
              if (!ofereceCobrarLote) return null;
              return (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--input-border)', background: 'var(--panel-bg)', cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={loteForm.ja_recebido}
                    onChange={(e) => setLoteForm({ ...loteForm, ja_recebido: e.target.checked })}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>
                    <strong>Já recebido — só registrar</strong> (não cobrar agora)
                    <br />
                    <span style={{ color: 'var(--text-muted)' }}>
                      O {ehPixSel ? 'Pix' : 'pagamento'} já caiu na conta (ex: Pix direto na Cora). Marque pra dar baixa na conta escolhida, sem gerar uma cobrança nova.
                    </span>
                  </span>
                </label>
              );
            })()}

            {(() => {
              const formaSelTipo = formasPagamento.find((f) => String(f.id) === loteForm.forma_pagamento_id)?.tipo ?? null;
              const ehCartaoSel = formaSelTipo === 'cartao_credito' || formaSelTipo === 'cartao_debito';
              if (!ehCartaoSel || !ofereceCobrarLote || loteForm.ja_recebido) return null;
              return <ImprimirViaCartao marcado={imprimirViaCartao} onChange={setImprimirViaCartao} />;
            })()}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secundario" onClick={() => setModalLote(false)} disabled={loteEnviando}>Cancelar</button>
              {(() => {
                // Forma Pix/Cartão + 1 cliente comum + provedor disponível e sem recebimento casado →
                // COBRA de verdade o total (peça 013/016). Senão, baixa na fé como hoje.
                const formaSelTipo = formasPagamento.find((f) => String(f.id) === loteForm.forma_pagamento_id)?.tipo ?? null;
                const ehPixSel = formaSelTipo === 'pix';
                const podeCobrarAgora = ofereceCobrarLote && !loteForm.ja_recebido;
                if (podeCobrarAgora) {
                  return (
                    <button
                      type="button" className="btn-primario" onClick={iniciarCobrancaLote} disabled={loteEnviando}
                      style={{ background: '#2563eb' }}
                    >
                      {ehPixSel ? 'Cobrar via Pix' : 'Cobrar no cartão'}
                    </button>
                  );
                }
                return (
              <button
                type="button"
                className="btn-primario"
                onClick={() => confirmarLote()}
                disabled={loteEnviando || (!lotePixSel && (!loteForm.forma_pagamento_id || !loteForm.caixa_id)) || (!!lotePixSel && totalLote > Number(lotePixSel.disponivel) + 0.005)}
                title={
                  !lotePixSel && (!loteForm.forma_pagamento_id || !loteForm.caixa_id) ? 'Escolha a forma e o caixa antes de confirmar'
                  : (!!lotePixSel && totalLote > Number(lotePixSel.disponivel) + 0.005) ? 'O total passa do valor do recebimento — ajuste antes de confirmar'
                  : undefined
                }
              >
                {loteEnviando ? 'Enviando...' : 'Confirmar Recebimento'}
              </button>
                );
              })()}
            </div>
          </div>
          </>
          )}
        </div>
      </Modal>

      {/* Modal Cancelar */}
      <Modal aberto={!!cancelarConta} onFechar={() => { setCancelarConta(null); setMotivoCancelamento(''); }} titulo="Cancelar Conta" largura={500}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cancelarConta && (
            <div style={{ fontSize: 14 }}>
              <div><strong>Cliente:</strong> {cancelarConta.cliente_nome}</div>
              <div><strong>Valor:</strong> <span className="cel-num">{formatMoeda(cancelarConta.valor)}</span></div>
              <div><strong>Vencimento:</strong> {formatDataSP(cancelarConta.data_vencimento)}</div>
            </div>
          )}
          <div>
            <label className="form-label">Motivo do cancelamento</label>
            <textarea
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
              rows={3}
              style={{ width: '100%' }}
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secundario" onClick={() => setCancelarConta(null)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={confirmarCancelamento}>Confirmar</button>
          </div>
        </div>
      </Modal>

      {/* Modal Estornar baixa — devolve o titulo pra em aberto (admin). O motivo e' obrigatorio e
          vai pro historico: estorno sem justificativa e' exatamente o buraco que isto fecha. */}
      <Modal aberto={!!estornarConta} onFechar={() => { setEstornarConta(null); setMotivoEstorno(''); }} titulo="Estornar Baixa" largura={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {estornarConta && (
            <>
              <div style={{ fontSize: 14 }}>
                <div><strong>Cliente:</strong> {estornarConta.cliente_nome}</div>
                <div><strong>Valor pago:</strong> <span className="cel-num">{formatMoeda(estornarConta.valor_pago ?? estornarConta.valor)}</span></div>
                <div><strong>Baixado em:</strong> {estornarConta.data_pagamento ? formatDataSP(estornarConta.data_pagamento) : '—'}</div>
              </div>
              <div style={{ fontSize: 13, background: 'var(--card-bg)', padding: 10, borderRadius: 6, lineHeight: 1.5 }}>
                O titulo volta pra <strong>em aberto</strong>. A entrada original no caixa <strong>nao e apagada</strong> —
                entra uma saida de mesmo valor no turno de hoje, pra o dia ja fechado continuar valendo.
                Se a baixa casou um Pix, ele volta a ficar disponivel na conciliacao.
              </div>
              <div>
                <label className="form-label">Motivo do estorno</label>
                <textarea
                  value={motivoEstorno}
                  onChange={(e) => setMotivoEstorno(e.target.value)}
                  rows={3}
                  style={{ width: '100%' }}
                  autoFocus
                />
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secundario" onClick={() => { setEstornarConta(null); setMotivoEstorno(''); }}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={confirmarEstorno} disabled={estornando}>
              {estornando ? 'Estornando...' : 'Confirmar Estorno'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Historico — a trilha de auditoria do titulo (migration 0210). */}
      <Modal aberto={!!historicoConta} onFechar={() => { setHistoricoConta(null); setHistoricoEventos(null); }} titulo="Historico do Titulo" largura={720}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {historicoConta && (
            <div style={{ fontSize: 14 }}>
              <div><strong>Cliente:</strong> {historicoConta.cliente_nome}</div>
              <div><strong>Titulo:</strong> CR-{historicoConta.id} · <span className="cel-num">{formatMoeda(historicoConta.valor)}</span></div>
            </div>
          )}
          {historicoEventos === null ? (
            <span style={{ color: 'var(--text-muted)' }}>Carregando...</span>
          ) : historicoEventos.length === 0 ? (
            <span style={{ color: 'var(--text-muted)' }}>Sem registros para este titulo.</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {historicoEventos.map((ev) => {
                const estimada = ev.origem_registro === 'reconstruido_titulo';
                return (
                  <div key={ev.id} style={{ background: 'var(--card-bg)', borderRadius: 6, padding: 10, fontSize: 13, lineHeight: 1.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <strong>{ROTULO_ACAO[ev.acao] ?? ev.acao}</strong>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {estimada
                          ? `${formatDataSP(ev.quando)} (hora nao registrada)`
                          : new Date(ev.quando).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div>
                      Por <strong>{ev.usuario_nome ?? 'nao registrado'}</strong>
                      {ev.gatilho ? ` · ${ROTULO_GATILHO[ev.gatilho] ?? ev.gatilho}` : ''}
                      {ev.valor != null ? <> · <span className="cel-num">{formatMoeda(ev.valor)}</span></> : null}
                      {ev.forma_nome ? ` · ${ev.forma_nome}` : ''}
                      {ev.caixa_nome ? ` · ${ev.caixa_nome}` : ''}
                    </div>
                    {ev.motivo && <div style={{ color: 'var(--text-muted)' }}>{ev.motivo}</div>}
                    {ev.ip && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>IP {ev.ip}</div>}
                    {ev.origem_registro !== 'live' && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {estimada
                          ? 'Reconstruido do titulo — so a data era conhecida.'
                          : 'Reconstruido da movimentacao de caixa — hora real.'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secundario" onClick={() => { setHistoricoConta(null); setHistoricoEventos(null); }}>Fechar</button>
          </div>
        </div>
      </Modal>

      {/* Modal Extrato preview — mesmo layout do HTML imprimivel (blocos por venda + itens) */}
      <Modal aberto={modalExtrato} onFechar={() => setModalExtrato(false)} titulo="Extrato de Titulos em Aberto" largura={1000}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {contasSelecionadas.length === 0 ? (
            <div style={{ color: 'var(--text-muted)' }}>Nenhuma conta selecionada</div>
          ) : (
            <>
              {/* Cabecalho fiscal */}
              <div style={{ textAlign: 'center', borderBottom: '1px solid var(--border, #e5e7eb)', paddingBottom: 8 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>EXTRATO DE TITULOS EM ABERTO</h2>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Fio de Cobre - Materiais Eletricos · CNPJ: 49.762.154/0001-00
                </div>
              </div>

              {/* Info do cliente */}
              <div style={{ fontSize: 14, borderTop: '1px solid var(--border, #e5e7eb)', borderBottom: '1px solid var(--border, #e5e7eb)', padding: '6px 0' }}>
                <div><strong>Cliente:</strong> {contasSelecionadas[0]?.cliente_nome}</div>
                {contasSelecionadas[0]?.cliente_cpf_cnpj && (
                  <div><strong>CPF/CNPJ:</strong> {contasSelecionadas[0]?.cliente_cpf_cnpj}</div>
                )}
                <div><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</div>
              </div>

              {/* Blocos por venda */}
              {[...contasSelecionadas]
                .sort((a, b) => (a.data_emissao || '').localeCompare(b.data_emissao || ''))
                .map((c, idx) => {
                  const info = itensPorCR[c.id] || { comprador: null, itens: [] };
                  const itens = info.itens || [];
                  return (
                    <div key={c.id} style={{ marginBottom: 4 }}>
                      <div style={{
                        background: 'var(--grid-row-alt-bg, #f3f4f6)',
                        padding: '6px 10px', fontWeight: 600, fontSize: 14,
                        borderRadius: 4, border: '1px solid var(--border, #e5e7eb)',
                      }}>
                        {idx + 1}. {info.comprador ? `Comprador: ${info.comprador} — ` : ''}
                        Orcamento {c.orcamento_numero || c.origem_legado || '-'}{info.nf_numero ? ` / NF ${info.nf_numero}` : ''} —
                        Emissao: {formatDataSP(c.data_emissao)} —
                        <span className="cel-num" style={{ color: 'var(--primary, #2563eb)' }}> {formatMoeda(c.valor)}</span>
                      </div>
                      {itens.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                              <th style={{ width: 36, padding: '4px' }}></th>
                              <th style={{ width: 70, padding: '4px', textAlign: 'left' }}>Cod</th>
                              <th style={{ padding: '4px', textAlign: 'left' }}>Produto</th>
                              <th style={{ width: 90, padding: '4px', textAlign: 'right' }}>Qtd</th>
                              <th style={{ width: 90, padding: '4px', textAlign: 'right' }}>Unit.</th>
                              <th style={{ width: 100, padding: '4px', textAlign: 'right' }}>Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itens.map((it, i) => (
                              <tr key={i} style={{ borderBottom: '1px dotted var(--border, #e5e7eb)' }}>
                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                  {it.foto_url ? (
                                    <img src={it.foto_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                                  ) : null}
                                </td>
                                <td style={{ padding: '4px', fontSize: 11, color: 'var(--text-muted)' }}>{it.codigo || ''}</td>
                                <td style={{ padding: '4px' }}>{it.produto}</td>
                                <td className="cel-num" style={{ padding: '4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  {it.qtd % 1 === 0 ? it.qtd : it.qtd.toFixed(3)} {it.unidade || ''}
                                </td>
                                <td className="cel-num" style={{ padding: '4px', textAlign: 'right' }}>{formatMoeda(it.preco)}</td>
                                <td className="cel-num" style={{ padding: '4px', textAlign: 'right' }}>{formatMoeda(it.subtotal)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ padding: 6, color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>
                          Itens nao disponiveis
                        </div>
                      )}
                    </div>
                  );
                })}

              {/* Total */}
              <div style={{
                borderTop: '2px solid var(--page-fg, #1f2937)',
                paddingTop: 8, marginTop: 4,
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 700, fontSize: 16,
              }}>
                <span>TOTAL</span>
                <span className="cel-num-forte">{formatMoeda(totalSelecionado)}</span>
              </div>

              {/* Acoes */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="btn-secundario" onClick={() => setModalExtrato(false)}>Fechar</button>
                <button type="button" className="btn-secundario" onClick={abrirEnviarWhatsApp}>
                  <Send size={14} style={{ display: 'inline', marginRight: 4 }} /> Enviar WhatsApp
                </button>
                <button type="button" className="btn-secundario" onClick={imprimirExtrato}>
                  <Printer size={14} style={{ display: 'inline', marginRight: 4 }} /> Imprimir
                </button>
                <button type="button" className="btn-primario" onClick={baixarExtratoPdf} disabled={baixandoExtratoPdf}>
                  {baixandoExtratoPdf
                    ? <><Loader2 size={14} className="animate-spin" style={{ display: 'inline', marginRight: 4 }} /> Gerando...</>
                    : <><FileDown size={14} style={{ display: 'inline', marginRight: 4 }} /> Salvar PDF</>}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Confirmar exclusao admin */}
      <ConfirmModal
        aberto={!!confirmarExcluir}
        titulo="Excluir conta?"
        mensagem={confirmarExcluir ? `Excluir permanentemente a conta de ${confirmarExcluir.cliente_nome} (${formatMoeda(confirmarExcluir.valor)})? Esta acao nao pode ser desfeita.` : ''}
        textoConfirmar="Excluir"
        onCancelar={() => setConfirmarExcluir(null)}
        onConfirmar={excluirConta}
        perigoso
      />

      {enviarWhatsAberto && contasSelecionadas.length > 0 && contasSelecionadas[0]!.cliente_id && (
        <ModalEnviarWhatsAppCliente
          titulo="Enviar Extrato pelo WhatsApp"
          clienteId={contasSelecionadas[0]!.cliente_id!}
          contextoNome={contasSelecionadas[0]!.cliente_nome}
          contextoExtra={`${contasSelecionadas.length} titulo(s) - ${formatMoeda(totalSelecionado)}`}
          onClose={() => { extratoEnvioRef.current = null; setEnviarWhatsAberto(false); }}
          enviarPara={async (digits) => {
            // Gera o JPG do extrato UMA vez (cache) e reusa pra cada destinatario.
            if (!extratoEnvioRef.current) {
              const cliente = contasSelecionadas[0]?.cliente_nome || 'Cliente';
              const cpf = contasSelecionadas[0]?.cliente_cpf_cnpj || '';
              const contas: ContaParaExtrato[] = contasSelecionadas.map((c) => ({
                id: c.id,
                orcamento_numero: c.orcamento_numero,
                data_emissao: c.data_emissao,
                valor: Number(c.valor),
              }));
              let extratoData = itensPorCR;
              const idsFaltando = contasSelecionadas.filter((c) => !extratoData[c.id]).map((c) => c.id);
              if (idsFaltando.length > 0) {
                const r = await api.get<ExtratoData>('/api/contas-receber/extrato-itens', { query: { ids: idsFaltando.join(',') } });
                extratoData = { ...extratoData, ...r };
                setItensPorCR(extratoData);
              }
              const paginas = await gerarExtratoJpg({ cliente, cliente_cpf_cnpj: cpf, contas, extratoData, totalSelecionado });
              extratoEnvioRef.current = { paginas, fileBase: cliente.replace(/\s+/g, '_') };
            }
            const { paginas, fileBase } = extratoEnvioRef.current;
            const cliente = contasSelecionadas[0]?.cliente_nome || 'Cliente';
            const infoBase = `*Extrato de titulos em aberto*\nCliente: ${cliente}`;
            for (let i = 0; i < paginas.length; i++) {
              const ehUltima = i === paginas.length - 1;
              const totalLinha = `\n*TOTAL: ${formatMoeda(totalSelecionado)}*`;
              const caption = paginas.length === 1
                ? `${infoBase}${totalLinha}`
                : ehUltima
                  ? `Pagina ${i + 1} de ${paginas.length}\n\n${infoBase}${totalLinha}`
                  : `Pagina ${i + 1} de ${paginas.length}\n\n${infoBase}`;
              await api.post('/api/contas-receber/enviar-whatsapp-extrato', {
                jid: digits,
                imagem_base64: paginas[i],
                filename: paginas.length === 1 ? `Extrato_${fileBase}.jpg` : `Extrato_${fileBase}_p${i + 1}.jpg`,
                caption,
              });
            }
          }}
        />
      )}

      {/* Enviar as NOTAS FISCAIS da baixa pelo WhatsApp — o PDF unico (uma nota por pagina) e uma
          legenda que RELACIONA os numeros das notas, pro cliente conferir sem abrir o arquivo. O
          servidor monta o pacote e a legenda; aqui so escolhemos os destinatarios (peca unica). */}
      {enviarNotasConta && (
        <ModalEnviarWhatsAppCliente
          titulo="Enviar notas fiscais pelo WhatsApp"
          clienteId={enviarNotasConta.cliente_id}
          contextoNome={enviarNotasConta.cliente_nome}
          contextoExtra={`${irmaosDaBaixa(enviarNotasConta).length} titulo(s) desta baixa`}
          arquivoLabel="Notas fiscais (PDF)"
          onClose={() => setEnviarNotasConta(null)}
          enviarPara={async (digits) => {
            await api.post('/api/nfe/danfes-lote/enviar-whatsapp', {
              conta_receber_ids: irmaosDaBaixa(enviarNotasConta).map((x) => x.id),
              jid: digits,
            });
          }}
        />
      )}

      {/* Enviar recibo pos-baixa pelo WhatsApp (todos os contatos com janela aberta) */}
      {reciboEnviar && (
        <ModalEnviarWhatsAppCliente
          titulo="Enviar recibo pelo WhatsApp"
          clienteId={reciboEnviar.clienteId}
          contextoNome={reciboEnviar.clienteNome}
          contextoExtra={`Recibo de ${formatMoeda(reciboEnviar.params.total_recebido)}`}
          arquivoLabel="Recibo de pagamento"
          onClose={() => { reciboJpgRef.current = null; setReciboEnviar(null); }}
          enviarPara={async (digits) => {
            // Gera o JPG do recibo UMA vez (cache) e reusa pra cada destinatario.
            if (!reciboJpgRef.current) reciboJpgRef.current = await gerarReciboJpg(reciboEnviar.params);
            const nome = reciboEnviar.clienteNome || 'cliente';
            await api.post('/api/contas-receber/enviar-whatsapp-extrato', {
              jid: digits,
              imagem_base64: reciboJpgRef.current,
              filename: `Recibo_${nome.replace(/\s+/g, '_')}.jpg`,
              caption: `*Recibo de pagamento*\nCliente: ${nome}\n*Total: ${formatMoeda(reciboEnviar.params.total_recebido)}*`,
            });
          }}
        />
      )}

      {/* Boleto no AppCel ([DA-629]/[DA-630]) — UM ou VARIOS titulos no mesmo papel */}
      <ModalBoletoTitulo
        titulos={boletoContas && boletoContas.map((c) => ({
          id: c.id,
          cliente_nome: c.cliente_nome,
          valor: c.valor,
          data_vencimento: c.data_vencimento,
        }))}
        onFechar={() => setBoletoContas(null)}
        onSalvo={() => { void carregar(); }}
      />

      {/* Seletor de cliente da aba Extrato */}
      {buscaClienteAberta && (
        <ModalBuscarCliente
          onClose={() => setBuscaClienteAberta(false)}
          onSelect={(c) => {
            setExtratoCliente({ id: c.id, nome: c.nome, codigo_legado: c.codigo_legado });
            setExtratoCarregado(false);
            setBuscaClienteAberta(false);
          }}
        />
      )}
    </div>
  );
}
